// FoodDetails.jsx - Full Updated Code with Dynamic Currency Symbol
import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import CurrencySymbol, { resolveCurrencyCode } from '../CurrencySymbol';
import './foodDetails.css';

const BASE_URL = '';

class ErrorBoundary extends React.Component {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Error in FoodDetails:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <div>Something went wrong. Please try again.</div>;
    }
    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
};

const FoodDetails = ({ item, cartItem, onClose, onUpdate }) => {
  if (!item) return null;

  // NEW: Currency Symbol Logic using resolveCurrencyCode
  const currencySymbol = useMemo(() => {
    const activeComp = localStorage.getItem("active_company");
    const activeBranch = localStorage.getItem("active_branch");
    const code = resolveCurrencyCode(null, activeComp, activeBranch);
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', marginRight: '2px', transform: 'translateY(1px)' }}>
        <CurrencySymbol currencyCode={code} size={14} />
      </span>
    );
  }, []);

  const [addonQuantities, setAddonQuantities] = useState(cartItem?.addonQuantities || {});
  const [comboQuantities, setComboQuantities] = useState(cartItem?.comboQuantities || {});
  const [selectedAddonVariants, setSelectedAddonVariants] = useState(cartItem?.addonVariants || {});
  const [selectedComboVariants, setSelectedComboVariants] = useState(cartItem?.comboVariants || {});
  const [selectedCombos, setSelectedCombos] = useState(cartItem?.selectedCombos || []);
  const [showCombos, setShowCombos] = useState(false);
  const [fetchedItem, setFetchedItem] = useState(null);
  const [addonIngredients, setAddonIngredients] = useState({});
  const [comboIngredients, setComboIngredients] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [quantity, setQuantity] = useState(cartItem?.quantity || 1);
  const [totalPrice, setTotalPrice] = useState(0);
  const [selectedVariants, setSelectedVariants] = useState({
    size: cartItem?.selectedSize || item.splitSize || (item.variants?.size?.enabled ? 'M' : null),
    cold: cartItem?.icePreference || item.icePreference || (item.variants?.cold?.enabled ? 'without_ice' : null),
    spicy: item.variants?.spicy?.enabled ? (cartItem?.isSpicy ?? item.isSpicy ?? false) : null,
    sugar: item.variants?.sugar?.enabled ? (cartItem?.sugarLevel || 'medium') : null,
  });
  const [selectedCustomVariants, setSelectedCustomVariants] = useState(cartItem?.selectedCustomVariants || {});
  const [selectedAddonCustomVariants, setSelectedAddonCustomVariants] = useState(cartItem?.addonCustomVariants || {});
  const [selectedComboCustomVariants, setSelectedComboCustomVariants] = useState(cartItem?.comboCustomVariants || {});
  const [showVariantPopup, setShowVariantPopup] = useState(false);
  const [showCustomVariantPopup, setShowCustomVariantPopup] = useState(false);
  // Unified popup state for addons and combos (stores name1 of the active item)
  const [showAddonAllVariantsPopup, setShowAddonAllVariantsPopup] = useState(null);
  const [showComboAllVariantsPopup, setShowComboAllVariantsPopup] = useState(null);
  // Legacy states kept for compatibility but unified popup replaces them
  const [showAddonVariantPopup, setShowAddonVariantPopup] = useState(null);
  const [showAddonCustomVariantPopup, setShowAddonCustomVariantPopup] = useState(null);
  const [showComboVariantPopup, setShowComboVariantPopup] = useState(null);
  const [showComboCustomVariantPopup, setShowComboCustomVariantPopup] = useState(null);
  // Toppings States
  const [allToppings, setAllToppings] = useState([]);
  const [selectedItemToppings, setSelectedItemToppings] = useState(cartItem?.itemToppings || {});
  const [selectedAddonToppings, setSelectedAddonToppings] = useState(cartItem?.addonToppings || {});
  const [selectedComboToppings, setSelectedComboToppings] = useState(cartItem?.comboToppings || {});
  const [showToppingsPopup, setShowToppingsPopup] = useState(false);
  const [showAddonToppingsPopup, setShowAddonToppingsPopup] = useState(null);
  const [showComboToppingsPopup, setShowComboToppingsPopup] = useState(null);
  const [selectedPreviewImage, setSelectedPreviewImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedSizeFilter, setSelectedSizeFilter] = useState(
    (cartItem?.selectedSize || item.splitSize)
      ? (cartItem?.selectedSize || item.splitSize) === 'S' ? 'small' : (cartItem?.selectedSize || item.splitSize) === 'M' ? 'medium' : 'large'
      : item.variants?.size?.enabled ? 'medium' : null
  );

  // NEW: Kitchen Notes State
  const [kitchenNotes, setKitchenNotes] = useState(cartItem?.kitchenNotes || {});
  const [showKitchenNoteModal, setShowKitchenNoteModal] = useState(false);
  const [hasInteractedWithVariants, setHasInteractedWithVariants] = useState(!!cartItem);

  // NEW: Keyboard Shortcuts Logic
  useEffect(() => {
    const isKeyboardShortcutsEnabled = localStorage.getItem("isKeyboardShortcutsEnabled") === "true";
    if (!isKeyboardShortcutsEnabled) return;

    const handleKeyDown = (e) => {
      const isAnyPopupOpen = showVariantPopup || showCustomVariantPopup || showAddonVariantPopup || showAddonCustomVariantPopup || showComboVariantPopup || showComboCustomVariantPopup || showKitchenNoteModal;

      if (e.key === 'Escape') {
        if (isAnyPopupOpen) {
          setShowVariantPopup(false);
          setShowCustomVariantPopup(false);
          setShowAddonVariantPopup(null);
          setShowAddonCustomVariantPopup(null);
          setShowComboVariantPopup(null);
          setShowComboCustomVariantPopup(null);
          setShowKitchenNoteModal(false);
        } else {
          onClose(); // Close FoodDetails modal
        }
        return;
      }

      const activeEl = document.activeElement;
      const isInput = activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA';

      if (isInput) return; // Don't trigger shortcuts when typing in an input

      switch (e.key) {
        case 'Enter':
          if (activeEl && activeEl.tagName !== 'BODY') {
            e.preventDefault();
            activeEl.click();
          } else if (!isAnyPopupOpen) {
            handleAddToCart();
          }
          break;

        case '+':
        case '=':
          if (!isAnyPopupOpen) {
            if (activeEl && activeEl.classList.contains('addon-item')) {
              const plusBtn = activeEl.parentElement.querySelector('.quantity-btn.plus');
              if (plusBtn) plusBtn.click();
            } else {
              increaseQuantity();
            }
          }
          break;
        case '-':
        case '_':
          if (!isAnyPopupOpen) {
            if (activeEl && activeEl.classList.contains('addon-item')) {
              const minusBtn = activeEl.parentElement.querySelector('.quantity-btn.minus');
              if (minusBtn) minusBtn.click();
            } else {
              decreaseQuantity();
            }
          }
          break;
        case 'ArrowRight':
        case 'ArrowLeft':
        case 'ArrowUp':
        case 'ArrowDown':
          if (activeEl && activeEl.tagName !== 'BODY') {
            e.preventDefault();
            const query = isAnyPopupOpen ? '.modal-overlay .variant-option' : '.addon-item';
            const focusableEls = Array.from(document.querySelectorAll(query));
            const currentIdx = focusableEls.indexOf(activeEl);
            if (currentIdx >= 0) {
              let nextIdx = currentIdx;
              if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                nextIdx = (currentIdx + 1) % focusableEls.length;
              } else {
                nextIdx = (currentIdx - 1 + focusableEls.length) % focusableEls.length;
              }
              focusableEls[nextIdx].focus();
            } else {
              if (focusableEls.length > 0) focusableEls[0].focus();
            }
          } else {
            const query = isAnyPopupOpen ? '.modal-overlay .variant-option' : '.addon-item';
            const focusableEls = Array.from(document.querySelectorAll(query));
            if (focusableEls.length > 0) focusableEls[0].focus();
          }
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showVariantPopup, showCustomVariantPopup, showAddonVariantPopup, showAddonCustomVariantPopup, showComboVariantPopup, showComboCustomVariantPopup, showKitchenNoteModal, showCombos, quantity, addonQuantities, comboQuantities, fetchedItem, showToppingsPopup, showAddonToppingsPopup, showComboToppingsPopup]);

  const hasActiveOffer = () => {
    return (
      fetchedItem?.offer_price !== undefined &&
      fetchedItem?.offer_price !== null &&
      fetchedItem?.offer_start_time &&
      fetchedItem?.offer_end_time &&
      new Date(fetchedItem.offer_start_time) <= new Date() &&
      new Date(fetchedItem.offer_end_time) > new Date()
    );
  };

  const calculateOfferSizePrice = (offerPrice, size) => {
    if (!offerPrice) return 0;
    switch (size) {
      case 'S': return offerPrice - 10;
      case 'M': return offerPrice;
      case 'L': return offerPrice + 10;
      default: return offerPrice;
    }
  };

  const calculateItemPrice = () => {
    if (!fetchedItem) return 0;
    if (hasActiveOffer()) {
      return calculateOfferSizePrice(fetchedItem.offer_price, selectedVariants.size);
    }
    if (fetchedItem.variants?.size?.enabled && selectedVariants.size) {
      return Number(selectedVariants.size === 'S' ? fetchedItem.variants.size.small_price : selectedVariants.size === 'M' ? fetchedItem.variants.size.medium_price : selectedVariants.size === 'L' ? fetchedItem.variants.size.large_price : fetchedItem.price_list_rate || 100);
    }
    return Number(fetchedItem.price_list_rate || 100);
  };

  // NEW: Fetch all toppings on mount
  useEffect(() => {
    const fetchToppings = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        };
        const response = await fetch(`${BASE_URL}/api/toppings`, { method: 'GET', headers });
        if (response.ok) {
          const data = await response.json();
          setAllToppings(data || []);
        }
      } catch (error) {
        console.error('Error fetching toppings:', error);
      }
    };
    fetchToppings();
  }, []);

  useEffect(() => {
    const fetchItemDetails = async () => {
      try {
        const itemName = encodeURIComponent(item.item_name || item.name);
        const active_company = localStorage.getItem('active_company');
        const active_branch = localStorage.getItem('active_branch');
        const token = localStorage.getItem('token');

        const headers = {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Company-Name': active_company || "",
          'X-Branch-Name': active_branch || ""
        };

        const response = await fetch(`${BASE_URL}/api/items/${itemName}`, {
          method: 'GET',
          headers,
        });
        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
        const data = await response.json();
        if (data) {
          const fetchedData = {
            ...data,
            item_name: data.item_name || item.item_name || item.name,
            item_group: item.item_group || data.item_group || item.category,
            price_list_rate: item.price_list_rate || data.price_list_rate || 100,
            image: data.image || item.image,
            uploaded_images: data.uploaded_images || item.uploaded_images || [],
            images: Array.isArray(data.images) ? data.images.map(img => img.startsWith('/api/images/') ? img : `/api/images/${img}`) : (item.images || []).map(img => img.startsWith('/api/images/') ? img : `/api/images/${img}`),
            ingredients: Array.isArray(data.ingredients) ? data.ingredients.map(ing => ({
              ...ing,
              base_price: ing.base_price || 100,
              nutrition: Array.isArray(ing.nutrition) ? ing.nutrition : [],
              weight: ing.weight || '1',
              small: ing.small || '1',
              medium: ing.medium || '1',
              large: ing.large || '1',
            })) : [],
            variants: {
              size: {
                enabled: data.variants?.size?.enabled ?? item.size?.enabled ?? false,
                small_price: data.variants?.size?.small_price ?? item.size?.small_price ?? 0,
                medium_price: data.variants?.size?.medium_price ?? item.size?.medium_price ?? 0,
                large_price: data.variants?.size?.large_price ?? item.size?.large_price ?? 0,
              },
              cold: data.variants?.cold || item.cold || { enabled: false, ice_price: 0 },
              spicy: {
                enabled: data.variants?.spicy?.enabled ?? item.spicy?.enabled ?? false,
                spicy_price: data.variants?.spicy?.spicy_price ?? item.spicy?.spicy_price ?? 30,
                non_spicy_price: data.variants?.spicy?.non_spicy_price ?? item.spicy?.non_spicy_price ?? 0,
                spicy_image: data.variants?.spicy?.spicy_image ?? item.spicy?.spicy_image,
                non_spicy_image: data.variants?.spicy?.non_spicy_image ?? item.spicy?.non_spicy_image,
              },
              sugar: data.variants?.sugar || item.sugar || { enabled: false, level: 'medium' },
            },
            custom_variants: Array.isArray(data.custom_variants) ? data.custom_variants.map(variant => ({
              ...variant,
              subheadings: Array.isArray(variant.subheadings) ? variant.subheadings : [],
            })) : item.custom_variants || [],
            addons: (Array.isArray(item.addons) && item.addons.length > 0) ? item.addons.map(propAddon => {
              const apiAddon = data.addons?.find(a => (a.name1 || a.name) === (propAddon.name1 || propAddon.name));
              const merged = { ...apiAddon, ...propAddon }; // Prop overrides API for basic fields, or vice versa? 
              // We want Props structure. API might have extra details.
              // Let's use Prop as base, and fill missing from API if match found.
              // Actually, safest to take Prop, and only overwrite specific fields if API has them and they match.
              // But simpler: Prop is Truth. API is extra.
              return {
                ...propAddon,
                // If API has variants, usage them.
                size: apiAddon?.size || propAddon.size || { enabled: false },
                cold: apiAddon?.cold || propAddon.cold || { enabled: false, ice_price: 0 },
                spicy: apiAddon?.spicy || propAddon.spicy || { enabled: false },
                sugar: apiAddon?.sugar || propAddon.sugar || { enabled: false },
                custom_variants: Array.isArray(apiAddon?.custom_variants) ? apiAddon.custom_variants.map(variant => ({
                  ...variant,
                  subheadings: Array.isArray(variant.subheadings) ? variant.subheadings : [],
                })) : (propAddon.custom_variants || []),
                // Image Priority: Prop > API
                addon_image: propAddon.addon_image || propAddon.image || apiAddon?.addon_image || apiAddon?.image,
              };
            }) : (data.addons || []).map(addon => ({
              ...addon,
              addon_image: addon.addon_image || addon.image,
            })),
            combos: (Array.isArray(item.combos) && item.combos.length > 0) ? item.combos.map(propCombo => {
              const apiCombo = data.combos?.find(c => (c.name1 || c.name) === (propCombo.name1 || propCombo.name));
              return {
                ...propCombo,
                size: apiCombo?.size || propCombo.size || { enabled: false },
                cold: apiCombo?.cold || propCombo.cold || { enabled: false, ice_price: 0 },
                spicy: apiCombo?.spicy || propCombo.spicy || { enabled: false },
                sugar: apiCombo?.sugar || propCombo.sugar || { enabled: false },
                custom_variants: Array.isArray(apiCombo?.custom_variants) ? apiCombo.custom_variants.map(variant => ({
                  ...variant,
                  subheadings: Array.isArray(variant.subheadings) ? variant.subheadings : [],
                })) : (propCombo.custom_variants || []),
                combo_image: propCombo.combo_image || propCombo.image || apiCombo?.combo_image || apiCombo?.image,
              };
            }) : (data.combos || []).map(combo => ({
              ...combo,
              combo_image: combo.combo_image || combo.image,
            })),
            offer_price: data.offer_price ?? item.offer_price,
            offer_start_time: data.offer_start_time ?? item.offer_start_time,
            offer_end_time: data.offer_end_time ?? item.offer_end_time,
            kitchen: data.kitchen || item.kitchen || 'Unknown',
          };
          setFetchedItem(fetchedData);
          setSelectedVariants({
            size: cartItem?.selectedSize || item.splitSize || (fetchedData.variants.size.enabled ? 'M' : null),
            cold: cartItem?.icePreference || item.icePreference || (fetchedData.variants.cold.enabled ? 'without_ice' : null),
            spicy: fetchedData.variants.spicy.enabled ? (cartItem?.isSpicy ?? item.isSpicy ?? false) : null,
            sugar: fetchedData.variants.sugar.enabled ? (cartItem?.sugarLevel || fetchedData.variants.sugar.level || 'medium') : null,
          });
          setSelectedSizeFilter(
            (cartItem?.selectedSize || item.splitSize)
              ? (cartItem?.selectedSize || item.splitSize) === 'S' ? 'small' : (cartItem?.selectedSize || item.splitSize) === 'M' ? 'medium' : 'large'
              : fetchedData.variants.size.enabled ? 'medium' : null
          );
          const initialPrice = fetchedData.price_list_rate || (fetchedData.variants.size.enabled ? fetchedData.variants.size.medium_price : fetchedData.price_list_rate);
          setTotalPrice((initialPrice || 100) * quantity);
          setSelectedPreviewImage(null);
          setCurrentImageIndex(0);
          // NEW: Load kitchen notes if editing
          setKitchenNotes(cartItem?.kitchenNotes || {});
        }
      } catch (error) {
        console.error('Error fetching item details:', error);
        const fallbackData = {
          ...item,
          item_name: item.item_name || item.name,
          item_group: item.item_group || item.category,
          ingredients: Array.isArray(item.ingredients) ? item.ingredients.map(ing => ({
            ...ing,
            base_price: ing.base_price || 100,
            nutrition: Array.isArray(ing.nutrition) ? ing.nutrition : [],
            weight: ing.weight || '1',
            small: ing.small || '1',
            medium: ing.medium || '1',
            large: ing.large || '1',
          })) : [],
          variants: {
            size: item.size || { enabled: false, small_price: 0, medium_price: 0, large_price: 0 },
            cold: item.cold || { enabled: false, ice_price: 0 },
            spicy: {
              enabled: item.spicy?.enabled ?? false,
              spicy_price: item.spicy?.spicy_price ?? 30,
              non_spicy_price: item.spicy?.non_spicy_price ?? 0,
              spicy_image: item.spicy?.spicy_image,
              non_spicy_image: item.spicy?.non_spicy_image,
            },
            sugar: item.sugar || { enabled: false, level: 'medium' },
          },
          custom_variants: Array.isArray(item.custom_variants) ? item.custom_variants : [],
          addons: Array.isArray(item.addons) ? item.addons.map(({ variants, ...addon }) => ({
            ...addon,
            cold: addon.cold || { enabled: false, ice_price: 0 },
            custom_variants: Array.isArray(addon.custom_variants) ? addon.custom_variants : [],
          })) : [],
          combos: Array.isArray(item.combos) ? item.combos.map(combo => ({
            ...combo,
            cold: combo.cold || { enabled: false, ice_price: 0 },
            custom_variants: Array.isArray(combo.custom_variants) ? combo.custom_variants : [],
          })) : [],
          uploaded_images: item.uploaded_images || [],
          images: Array.isArray(item.images) ? item.images : [],
          price_list_rate: item.price_list_rate || 100,
          offer_price: item.offer_price ?? null,
          offer_start_time: item.offer_start_time ?? null,
          offer_end_time: item.offer_end_time ?? null,
          kitchen: item.kitchen || 'Unknown',
        };
        setFetchedItem(fallbackData);
        setSelectedSizeFilter(
          (cartItem?.selectedSize || item.splitSize)
            ? (cartItem?.selectedSize || item.splitSize) === 'S' ? 'small' : (cartItem?.selectedSize || item.splitSize) === 'M' ? 'medium' : 'large'
            : item.size?.enabled ? 'medium' : null
        );
        
        setSelectedPreviewImage(null);
        setCurrentImageIndex(0);
        // NEW: Load kitchen notes if editing
        setKitchenNotes(cartItem?.kitchenNotes || {});
      }
    };
    fetchItemDetails();
  }, [item, cartItem]);

  useEffect(() => {
    const fetchAddonAndComboIngredients = async () => {
      if (!fetchedItem) return;
      const newAddonIngredients = {};
      for (const addon of fetchedItem.addons || []) {
        try {
          const active_company = localStorage.getItem('active_company');
          const active_branch = localStorage.getItem('active_branch');
          const token = localStorage.getItem('token');

          const headers = {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'X-Company-Name': active_company || "",
            'X-Branch-Name': active_branch || ""
          };

          const response = await fetch(
            `${BASE_URL}/api/items/nutrition/${encodeURIComponent(addon.name1)}?type=addon&item_id=${fetchedItem._id}&index=${fetchedItem.addons.indexOf(addon)}`,
            {
              method: 'GET',
              headers,
            }
          );
          if (response.ok) {
            const data = await response.json();
            newAddonIngredients[addon.name1] = Array.isArray(data.ingredients) ? data.ingredients.map(ing => ({
              ...ing,
              nutrition: Array.isArray(ing.nutrition) ? ing.nutrition : [],
              weight: ing.weight || '1',
              small: ing.small || '1',
              medium: ing.medium || '1',
              large: ing.large || '1',
            })) : [];
          } else {
            console.warn(`Failed to fetch ingredients for add-on ${addon.name1}`);
            newAddonIngredients[addon.name1] = [];
          }
        } catch (error) {
          console.error(`Error fetching ingredients for add-on ${addon.name1}:`, error);
          newAddonIngredients[addon.name1] = [];
        }
      }
      setAddonIngredients(newAddonIngredients);
      const newComboIngredients = {};
      for (const combo of fetchedItem.combos || []) {
        try {
          const active_company = localStorage.getItem('active_company');
          const active_branch = localStorage.getItem('active_branch');
          const token = localStorage.getItem('token');

          const headers = {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'X-Company-Name': active_company || "",
            'X-Branch-Name': active_branch || ""
          };

          const response = await fetch(
            `${BASE_URL}/api/items/nutrition/${encodeURIComponent(combo.name1)}?type=combo&item_id=${fetchedItem._id}&index=${fetchedItem.combos.indexOf(combo)}`,
            {
              method: 'GET',
              headers,
            }
          );
          if (response.ok) {
            const data = await response.json();
            newComboIngredients[combo.name1] = Array.isArray(data.ingredients) ? data.ingredients.map(ing => ({
              ...ing,
              nutrition: Array.isArray(ing.nutrition) ? ing.nutrition : [],
              weight: ing.weight || '1',
              small: ing.small || '1',
              medium: ing.medium || '1',
              large: ing.large || '1',
            })) : [];
          } else {
            console.warn(`Failed to fetch ingredients for combo ${combo.name1}`);
            newComboIngredients[combo.name1] = [];
          }
        } catch (error) {
          console.error(`Error fetching ingredients for combo ${combo.name1}:`, error);
          newComboIngredients[combo.name1] = [];
        }
      }
      setComboIngredients(newComboIngredients);
    };
    fetchAddonAndComboIngredients();
  }, [fetchedItem, addonQuantities, comboQuantities]);

  useEffect(() => {
    if (!fetchedItem) return;
    let itemPrice = calculateItemPrice();
    if (fetchedItem.variants?.cold?.enabled && selectedVariants.cold === 'with_ice') {
      itemPrice += Number(fetchedItem.variants.cold.ice_price || 0);
    }
    if (fetchedItem.variants?.spicy?.enabled && selectedVariants.spicy) {
      itemPrice += Number(fetchedItem.variants.spicy.spicy_price || 30);
    }
    itemPrice *= quantity;
    const addonsPrice = Object.entries(addonQuantities).reduce((sum, [addonName, qty]) => {
      if (qty <= 0) return sum;
      const addon = fetchedItem.addons?.find(a => a.name1 === addonName);
      if (!addon) return sum;
      const variants = selectedAddonVariants[addonName] || {};
      let price = Number(addon.size?.enabled ? addon.size.medium_price : addon.addon_price || 0);
      if (addon.size?.enabled && variants.size) {
        price = Number(variants.size === 'S' ? addon.size.small_price : variants.size === 'M' ? addon.size.medium_price : variants.size === 'L' ? addon.size.large_price : price);
      }
      if (addon.cold?.enabled && variants.cold === 'with_ice') {
        price += Number(addon.cold.ice_price || 0);
      }
      if (addon.spicy?.enabled && variants.spicy) {
        price += Number(addon.spicy.spicy_price || 30);
      }
      
      const addonToppingsPrice = Object.entries(selectedAddonToppings[addonName] || {}).reduce((tSum, [toppingId, isSelected]) => {
        if (isSelected) {
          const topping = allToppings.find(t => t._id === toppingId || t.topping_name === toppingId);
          return tSum + Number(topping?.price || 0);
        }
        return tSum;
      }, 0);

      const customVariantsPrice = addon.custom_variants?.length > 0
        ? addon.custom_variants.reduce((variantSum, variant) => {
          if (!variant.enabled) return variantSum;
          return variantSum + variant.subheadings.reduce((subSum, sub) => {
            if (selectedAddonCustomVariants[addonName]?.[sub.name]) {
              return subSum + Number(sub.price || 0);
            }
            return subSum;
          }, 0);
        }, 0)
        : 0;
      return sum + (price + customVariantsPrice + addonToppingsPrice) * qty;
    }, 0);
    const combosPrice = Object.entries(comboQuantities).reduce((sum, [comboName, qty]) => {
      if (qty <= 0) return sum;
      const combo = fetchedItem.combos?.find(c => c.name1 === comboName);
      if (!combo) return sum;
      const variants = selectedComboVariants[comboName] || {};
      let price = Number(combo.size?.enabled ? combo.size.medium_price : combo.combo_price || 0);
      if (combo.size?.enabled && variants.size) {
        price = Number(variants.size === 'S' ? combo.size.small_price : variants.size === 'M' ? combo.size.medium_price : variants.size === 'L' ? combo.size.large_price : price);
      }
      if (combo.cold?.enabled && variants.cold === 'with_ice') {
        price += Number(combo.cold.ice_price || 0);
      }
      if (combo.spicy?.enabled && variants.spicy) {
        price += Number(combo.spicy.spicy_price || 30);
      }

      const comboToppingsPrice = Object.entries(selectedComboToppings[comboName] || {}).reduce((tSum, [toppingId, isSelected]) => {
        if (isSelected) {
          const topping = allToppings.find(t => t._id === toppingId || t.topping_name === toppingId);
          return tSum + Number(topping?.price || 0);
        }
        return tSum;
      }, 0);

      const customVariantsPrice = combo.custom_variants?.length > 0
        ? combo.custom_variants.reduce((variantSum, variant) => {
          if (!variant.enabled) return variantSum;
          return variantSum + variant.subheadings.reduce((subSum, sub) => {
            if (selectedComboCustomVariants[comboName]?.[sub.name]) {
              return subSum + Number(sub.price || 0);
            }
            return subSum;
          }, 0);
        }, 0)
        : 0;
      return sum + (price + customVariantsPrice + comboToppingsPrice) * qty;
    }, 0);
    const customVariantsPrice = fetchedItem.custom_variants?.length > 0
      ? fetchedItem.custom_variants.reduce((sum, variant) => {
        if (!variant.enabled) return sum;
        return sum + variant.subheadings.reduce((variantSum, sub) => {
          if (selectedCustomVariants[sub.name]) {
            return variantSum + Number(sub.price || 0);
          }
          return variantSum;
        }, 0);
      }, 0) * quantity
      : 0;
    
    const itemToppingsPrice = Object.entries(selectedItemToppings).reduce((sum, [toppingId, isSelected]) => {
      if (isSelected) {
        const topping = allToppings.find(t => t._id === toppingId || t.topping_name === toppingId);
        return sum + (topping?.price || 0);
      }
      return sum;
    }, 0) * quantity;

    setTotalPrice(itemPrice + addonsPrice + combosPrice + customVariantsPrice + itemToppingsPrice);
  }, [
    fetchedItem,
    quantity,
    selectedVariants,
    addonQuantities,
    selectedAddonVariants,
    selectedAddonCustomVariants,
    comboQuantities,
    selectedComboVariants,
    selectedComboCustomVariants,
    selectedCustomVariants,
    allToppings,
    selectedItemToppings,
    selectedAddonToppings,
    selectedComboToppings
  ]);

  const getCurrentItemPrice = () => {
    if (!fetchedItem) return item?.price_list_rate || 100;
    return calculateItemPrice();
  };

  const handleVariantChange = (type, value) => {
    setHasInteractedWithVariants(true);
    setSelectedVariants(prev => ({ ...prev, [type]: value }));
    if (type === 'size') {
      setSelectedSizeFilter(value === 'S' ? 'small' : value === 'M' ? 'medium' : value === 'L' ? 'large' : null);
    }
  };

  const handleCustomVariantChange = (name) => {
    setHasInteractedWithVariants(true);
    setSelectedCustomVariants(prev => ({
      ...prev,
      [name]: !prev[name],
    }));
  };

  const handleAddonCustomVariantChange = (addonName, subName) => {
    setSelectedAddonCustomVariants(prev => ({
      ...prev,
      [addonName]: {
        ...prev[addonName],
        [subName]: !prev[addonName]?.[subName],
      },
    }));
  };

  const handleComboCustomVariantChange = (comboName, subName) => {
    setSelectedComboCustomVariants(prev => ({
      ...prev,
      [comboName]: {
        ...prev[comboName],
        [subName]: !prev[comboName]?.[subName],
      },
    }));
  };

  const handleAddonCheck = (addon, checked) => {
    setAddonQuantities(prev => ({
      ...prev,
      [addon.name1]: checked ? 1 : 0,
    }));
    setSelectedAddonVariants(prev => ({
      ...prev,
      [addon.name1]: checked ? {
        size: addon.size?.enabled ? 'M' : null,
        cold: addon.cold?.enabled ? 'without_ice' : null,
        spicy: addon.spicy?.enabled ? false : null,
        sugar: addon.sugar?.enabled ? 'medium' : null,
      } : prev[addon.name1] || {},
    }));
    if (!checked) {
      setSelectedAddonCustomVariants(prev => {
        const { [addon.name1]: _, ...rest } = prev;
        return rest;
      });
      setKitchenNotes(prev => {
        const { [addon.name1]: _, ...rest } = prev;
        return rest;
      });
      setShowAddonAllVariantsPopup(null);
    } else {
      // Auto-open unified popup if this addon has any variants/toppings
      const hasVariants = addon.size?.enabled || addon.cold?.enabled || addon.spicy?.enabled || addon.sugar?.enabled;
      const hasCustom = addon.custom_variants?.some(v => v.enabled);
      const hasToppings = addon.toppings?.length > 0;
      if (hasVariants || hasCustom || hasToppings) {
        setShowAddonAllVariantsPopup(addon.name1);
      }
    }
  };

  const handleAddonVariantChange = (addonName, variantType, value) => {
    setSelectedAddonVariants(prev => {
      const current = prev[addonName] || {};
      return { ...prev, [addonName]: { ...current, [variantType]: value } };
    });
  };

  const increaseAddonQuantity = addonName => {
    setAddonQuantities(prev => ({ ...prev, [addonName]: (prev[addonName] || 0) + 1 }));
  };

  const decreaseAddonQuantity = addonName => {
    setAddonQuantities(prev => {
      const newQty = Math.max((prev[addonName] || 0) - 1, 0);
      if (newQty === 0) {
        setSelectedAddonVariants(prev => {
          const { [addonName]: _, ...rest } = prev;
          return rest;
        });
        setSelectedAddonCustomVariants(prev => {
          const { [addonName]: _, ...rest } = prev;
          return rest;
        });
        // NEW: Clear notes for removed addon
        setKitchenNotes(prev => {
          const { [addonName]: _, ...rest } = prev;
          return rest;
        });
      }
      return { ...prev, [addonName]: newQty };
    });
  };

  const handleComboCheck = combo => {
    const isSelected = comboQuantities[combo.name1] > 0;
    if (!isSelected) {
      setComboQuantities(prev => ({ ...prev, [combo.name1]: 1 }));
      setSelectedComboVariants(prev => ({
        ...prev,
        [combo.name1]: {
          size: combo.size?.enabled ? 'M' : null,
          cold: combo.cold?.enabled ? 'without_ice' : null,
          spicy: combo.spicy?.enabled ? false : null,
          sugar: combo.sugar?.enabled ? 'medium' : null,
        },
      }));
      setSelectedCombos(prev => [
        ...prev.filter(c => c.name1 !== combo.name1),
        {
          ...combo,
          variant: {
            size: combo.size?.enabled ? 'M' : null,
            cold: combo.cold?.enabled ? 'without_ice' : null,
            spicy: combo.spicy?.enabled ? false : null,
            sugar: combo.sugar?.enabled ? 'medium' : null,
          },
        },
      ]);
      // Auto-open unified popup if this combo has any variants/toppings
      const hasVariants = combo.size?.enabled || combo.cold?.enabled || combo.spicy?.enabled || combo.sugar?.enabled;
      const hasCustom = combo.custom_variants?.some(v => v.enabled);
      const hasToppings = combo.toppings?.length > 0;
      if (hasVariants || hasCustom || hasToppings) {
        setShowComboAllVariantsPopup(combo.name1);
      }
    } else {
      // Deselect
      setComboQuantities(prev => ({ ...prev, [combo.name1]: 0 }));
      setSelectedCombos(prev => prev.filter(c => c.name1 !== combo.name1));
      setShowComboAllVariantsPopup(null);
    }
  };

  const handleComboVariantChange = (comboName, variantType, value) => {
    setSelectedComboVariants(prev => {
      const current = prev[comboName] || {};
      return { ...prev, [comboName]: { ...current, [variantType]: value } };
    });
  };

  const increaseComboQuantity = comboName => {
    setComboQuantities(prev => ({ ...prev, [comboName]: (prev[comboName] || 0) + 1 }));
  };

  const decreaseComboQuantity = comboName => {
    setComboQuantities(prev => {
      const newQty = Math.max((prev[comboName] || 0) - 1, 0);
      if (newQty === 0) {
        setSelectedCombos(prev => prev.filter(c => c.name1 !== comboName));
        setSelectedComboVariants(prev => {
          const { [comboName]: _, ...rest } = prev;
          return rest;
        });
        setSelectedComboCustomVariants(prev => {
          const { [comboName]: _, ...rest } = prev;
          return rest;
        });
        // NEW: Clear notes for removed combo
        setKitchenNotes(prev => {
          const { [comboName]: _, ...rest } = prev;
          return rest;
        });
      }
      return { ...prev, [comboName]: newQty };
    });
  };

  // NEW: Handle Kitchen Notes Change
  const handleKitchenNoteChange = (key, value) => {
    setKitchenNotes(prev => ({ ...prev, [key]: value }));
  };

  // NEW: Save Kitchen Notes
  const saveKitchenNotes = () => {
    setShowKitchenNoteModal(false);
  };

  const handleAddToCart = () => {
    if (!fetchedItem) return;
    let basePrice = calculateItemPrice();
    let icePrice = 0;
    let spicyPrice = 0;
    if (fetchedItem.variants?.cold?.enabled && selectedVariants.cold === 'with_ice') {
      icePrice = fetchedItem.variants.cold.ice_price || 0;
    }
    if (fetchedItem.variants?.spicy?.enabled && selectedVariants.spicy) {
      spicyPrice = fetchedItem.variants.spicy.spicy_price || 30;
    }
    const filteredAddonQuantities = {};
    const filteredAddonVariants = {};
    const filteredAddonCustomVariants = {};
    const addonPrices = {};
    const addonCustomVariantsDetails = {};
    Object.entries(addonQuantities).forEach(([addonName, qty]) => {
      if (qty > 0) {
        filteredAddonQuantities[addonName] = qty;
        const addon = fetchedItem.addons?.find(a => a.name1 === addonName);
        if (!addon) return;
        const variants = selectedAddonVariants[addonName] || {};
        let price = Number(addon.size?.enabled ? addon.size.medium_price : addon.addon_price || 0);
        if (addon.size?.enabled && variants.size) {
          price = Number(variants.size === 'S' ? addon.size.small_price : variants.size === 'M' ? addon.size.medium_price : variants.size === 'L' ? addon.size.large_price : price);
        }
        if (addon.cold?.enabled && variants.cold === 'with_ice') {
          price += Number(addon.cold.ice_price || 0);
        }
        if (addon.spicy?.enabled && variants.spicy) {
          price += Number(addon.spicy.spicy_price || 30);
        }
        const customVariantsPrice = addon.custom_variants?.length > 0
          ? addon.custom_variants.reduce((variantSum, variant) => {
            if (!variant.enabled) return variantSum;
            return variantSum + variant.subheadings.reduce((subSum, sub) => {
              if (selectedAddonCustomVariants[addonName]?.[sub.name]) {
                return subSum + Number(sub.price || 0);
              }
              return subSum;
            }, 0);
          }, 0)
          : 0;
        filteredAddonVariants[addonName] = variants;
        filteredAddonCustomVariants[addonName] = selectedAddonCustomVariants[addonName] || {};
        addonPrices[addonName] = price + customVariantsPrice;
        if (addon.custom_variants?.length > 0) {
          addonCustomVariantsDetails[addonName] = {};
          addon.custom_variants.forEach(variant => {
            if (variant.enabled) {
              variant.subheadings.forEach(sub => {
                if (selectedAddonCustomVariants[addonName]?.[sub.name]) {
                  addonCustomVariantsDetails[addonName][sub.name] = {
                    name: sub.name,
                    price: sub.price || 0,
                    heading: variant.heading,
                    image: sub.image,
                  };
                }
              });
            }
          });
        }
      }
    });
    const filteredComboQuantities = {};
    const filteredComboVariants = {};
    const filteredComboCustomVariants = {};
    const comboPrices = {};
    const comboCustomVariantsDetails = {};
    Object.entries(comboQuantities).forEach(([comboName, qty]) => {
      if (qty > 0) {
        filteredComboQuantities[comboName] = qty;
        const combo = fetchedItem.combos?.find(c => c.name1 === comboName);
        if (!combo) return;
        const variants = selectedComboVariants[comboName] || {};
        let price = Number(combo.size?.enabled ? combo.size.medium_price : combo.combo_price || 0);
        if (combo.size?.enabled && variants.size) {
          price = Number(variants.size === 'S' ? combo.size.small_price : variants.size === 'M' ? combo.size.medium_price : variants.size === 'L' ? combo.size.large_price : price);
        }
        if (combo.cold?.enabled && variants.cold === 'with_ice') {
          price += Number(combo.cold.ice_price || 0);
        }
        if (combo.spicy?.enabled && variants.spicy) {
          price += Number(combo.spicy.spicy_price || 30);
        }
        const customVariantsPrice = combo.custom_variants?.length > 0
          ? combo.custom_variants.reduce((variantSum, variant) => {
            if (!variant.enabled) return variantSum;
            return variantSum + variant.subheadings.reduce((subSum, sub) => {
              if (selectedComboCustomVariants[comboName]?.[sub.name]) {
                return subSum + Number(sub.price || 0);
              }
              return subSum;
            }, 0);
          }, 0)
          : 0;
        filteredComboVariants[comboName] = variants;
        filteredComboCustomVariants[comboName] = selectedComboCustomVariants[comboName] || {};
        comboPrices[comboName] = price + customVariantsPrice;
        if (combo.custom_variants?.length > 0) {
          comboCustomVariantsDetails[comboName] = {};
          combo.custom_variants.forEach(variant => {
            if (variant.enabled) {
              variant.subheadings.forEach(sub => {
                if (selectedComboCustomVariants[comboName]?.[sub.name]) {
                  comboCustomVariantsDetails[comboName][sub.name] = {
                    name: sub.name,
                    price: sub.price || 0,
                    heading: variant.heading,
                    image: sub.image,
                  };
                }
              });
            }
          });
        }
      }
    });
    const customVariantsDetails = {};
    const customVariantsQuantities = {};
    if (selectedCustomVariants && fetchedItem.custom_variants) {
      fetchedItem.custom_variants.forEach(variant => {
        if (variant.enabled) {
          variant.subheadings.forEach(sub => {
            if (selectedCustomVariants[sub.name]) {
              customVariantsDetails[sub.name] = {
                name: sub.name,
                price: sub.price || 0,
                heading: variant.heading,
                image: sub.image,
              };
              customVariantsQuantities[sub.name] = 1; // Default quantity for main item custom variants
            }
          });
        }
      });
    }

    const customizedItem = {
      _id: fetchedItem._id,
      item_name: fetchedItem.item_name || fetchedItem.name,
      type: fetchedItem.type || (fetchedItem.item_group?.toLowerCase().includes('addon') ? 'addon' : fetchedItem.item_group?.toLowerCase().includes('combo') ? 'item_combo' : 'item'),
      item_group: fetchedItem.item_group,
      image: fetchedItem.image,
      images: fetchedItem.images,
      basePrice,
      offer_price: fetchedItem.offer_price,
      offer_start_time: fetchedItem.offer_start_time,
      offer_end_time: fetchedItem.offer_end_time,
      variants: {
        size: fetchedItem.variants?.size?.enabled ? { selected: selectedVariants.size, prices: fetchedItem.variants.size } : null,
        cold: fetchedItem.variants?.cold?.enabled ? { icePreference: selectedVariants.cold, ice_price: fetchedItem.variants.cold.ice_price } : null,
        spicy: fetchedItem.variants?.spicy?.enabled ? {
          isSpicy: selectedVariants.spicy,
          spicy_price: fetchedItem.variants.spicy.spicy_price,
          non_spicy_price: fetchedItem.variants.spicy.non_spicy_price,
          spicy_image: fetchedItem.variants.spicy.spicy_image,
          non_spicy_image: fetchedItem.variants.spicy.non_spicy_image,
        } : null,
        sugar: fetchedItem.variants?.sugar?.enabled ? { level: selectedVariants.sugar } : null,
      },
      selectedCustomVariants,
      customVariantsDetails,
      customVariantsQuantities,
      addonQuantities: filteredAddonQuantities,
      addonVariants: filteredAddonVariants,
      addonCustomVariants: filteredAddonCustomVariants,
      addonPrices,
      addonCustomVariantsDetails,
      comboQuantities: filteredComboQuantities,
      comboVariants: filteredComboVariants,
      comboCustomVariants: filteredComboCustomVariants,
      comboPrices,
      comboCustomVariantsDetails,
      selectedCombos: selectedCombos.map(combo => ({
        name1: combo.name1,
        price: comboPrices[combo.name1] || (combo.size?.enabled ? combo.size.medium_price : combo.combo_price || 0),
        combo_image: combo.combo_image,
        variant: combo.variant,
        custom_variants: selectedComboCustomVariants[combo.name1] || {},
        quantity: comboQuantities[combo.name1] || 0,
      })),
      kitchen: fetchedItem.kitchen,
      quantity,
      totalPrice,
      icePrice,
      spicyPrice,
      isSpicy: selectedVariants.spicy,
      ingredients: fetchedItem.ingredients,
      nutrition: fetchedItem.nutrition,
      // NEW: Include kitchen notes
      kitchenNotes,
      // NEW: Include Toppings with Details
      itemToppings: Object.entries(selectedItemToppings).reduce((acc, [id, isSelected]) => {
        if (isSelected) {
          const topping = allToppings.find(t => t._id === id || t.topping_name === id);
          if (topping) acc[id] = { name: topping.topping_name, price: topping.price || 0 };
        }
        return acc;
      }, {}),
      addonToppings: Object.entries(selectedAddonToppings).reduce((acc, [addonName, toppings]) => {
        acc[addonName] = Object.entries(toppings).reduce((tAcc, [id, isSelected]) => {
          if (isSelected) {
            const topping = allToppings.find(t => t._id === id || t.topping_name === id);
            if (topping) tAcc[id] = { name: topping.topping_name, price: topping.price || 0 };
          }
          return tAcc;
        }, {});
        return acc;
      }, {}),
      comboToppings: Object.entries(selectedComboToppings).reduce((acc, [comboName, toppings]) => {
        acc[comboName] = Object.entries(toppings).reduce((tAcc, [id, isSelected]) => {
          if (isSelected) {
            const topping = allToppings.find(t => t._id === id || t.topping_name === id);
            if (topping) tAcc[id] = { name: topping.topping_name, price: topping.price || 0 };
          }
          return tAcc;
        }, {});
        return acc;
      }, {}),
    };
    onUpdate(customizedItem);
    onClose();
  };

  const increaseQuantity = () => setQuantity(prev => prev + 1);
  const decreaseQuantity = () => setQuantity(prev => (prev > 1 ? prev - 1 : 1));

  const getCurrentAddonPrice = addon => {
    if (!addon) return 0;
    const variants = selectedAddonVariants[addon.name1] || {};
    let price = Number(addon.size?.enabled ? addon.size.medium_price : addon.addon_price || 0);
    if (addon.size?.enabled && variants.size) {
      price = Number(variants.size === 'S' ? addon.size.small_price : variants.size === 'M' ? addon.size.medium_price : variants.size === 'L' ? addon.size.large_price : price);
    }
    if (addon.cold?.enabled && variants.cold === 'with_ice') {
      price += Number(addon.cold.ice_price || 0);
    }
    if (addon.spicy?.enabled && variants.spicy) {
      price += Number(addon.spicy.spicy_price || 30);
    }
    const customVariantsPrice = addon.custom_variants?.length > 0
      ? addon.custom_variants.reduce((variantSum, variant) => {
        if (!variant.enabled) return variantSum;
        return variantSum + variant.subheadings.reduce((subSum, sub) => {
          if (selectedAddonCustomVariants[addon.name1]?.[sub.name]) {
            return subSum + Number(sub.price || 0);
          }
          return subSum;
        }, 0);
      }, 0)
      : 0;

    const addonToppingsPrice = Object.entries(selectedAddonToppings[addon.name1] || {}).reduce((tSum, [toppingId, isSelected]) => {
      if (isSelected) {
        const topping = allToppings.find(t => t._id === toppingId || t.topping_name === toppingId);
        return tSum + Number(topping?.price || 0);
      }
      return tSum;
    }, 0);

    return price + customVariantsPrice + addonToppingsPrice;
  };

  const getCurrentComboPrice = combo => {
    if (!combo) return 0;
    const variants = selectedComboVariants[combo.name1] || {};
    let price = Number(combo.size?.enabled ? combo.size.medium_price : combo.combo_price || 0);
    if (combo.size?.enabled && variants.size) {
      price = Number(variants.size === 'S' ? combo.size.small_price : variants.size === 'M' ? combo.size.medium_price : variants.size === 'L' ? combo.size.large_price : price);
    }
    if (combo.cold?.enabled && variants.cold === 'with_ice') {
      price += Number(combo.cold.ice_price || 0);
    }
    if (combo.spicy?.enabled && variants.spicy) {
      price += Number(combo.spicy.spicy_price || 30);
    }
    const customVariantsPrice = combo.custom_variants?.length > 0
      ? combo.custom_variants.reduce((variantSum, variant) => {
        if (!variant.enabled) return variantSum;
        return variantSum + variant.subheadings.reduce((subSum, sub) => {
          if (selectedComboCustomVariants[combo.name1]?.[sub.name]) {
            return subSum + Number(sub.price || 0);
          }
          return subSum;
        }, 0);
      }, 0)
      : 0;

    const comboToppingsPrice = Object.entries(selectedComboToppings[combo.name1] || {}).reduce((tSum, [toppingId, isSelected]) => {
      if (isSelected) {
        const topping = allToppings.find(t => t._id === toppingId || t.topping_name === toppingId);
        return tSum + Number(topping?.price || 0);
      }
      return tSum;
    }, 0);

    return price + customVariantsPrice + comboToppingsPrice;
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return '';
    if (imagePath.startsWith('http') || imagePath.startsWith('data:')) return imagePath;

    // Handle paths that are already relative to root
    if (imagePath.startsWith('/')) {
      return `${BASE_URL}${imagePath}`;
    }

    // Handle paths that start with api/ but no leading slash
    if (imagePath.startsWith('api/')) {
      return `${BASE_URL}/${imagePath}`;
    }

    // Default: Assume it's a filename in /api/images/
    return `${BASE_URL}/api/images/${imagePath}`;
  };

  const handlePrevImage = () => {
    const allImages = [fetchedItem.image, ...(fetchedItem.uploaded_images || []), ...(fetchedItem.images || [])].filter(Boolean).map(getImageUrl);
    if (!allImages.length) return;
    const newIndex = (currentImageIndex - 1 + allImages.length) % allImages.length;
    setCurrentImageIndex(newIndex);
    setSelectedPreviewImage(allImages[newIndex]);
  };

  const handleNextImage = () => {
    const allImages = [fetchedItem.image, ...(fetchedItem.uploaded_images || []), ...(fetchedItem.images || [])].filter(Boolean).map(getImageUrl);
    if (!allImages.length) return;
    const newIndex = (currentImageIndex + 1) % allImages.length;
    setCurrentImageIndex(newIndex);
    setSelectedPreviewImage(allImages[newIndex]);
  };

  const handleClose = () => {
    setSelectedPreviewImage(null);
    setCurrentImageIndex(0);
    setSelectedSizeFilter(null);
    onClose();
  };

  const calculateNutrition = (ingredient, sizeKey, isAddon = false, addonName = null, isCombo = false, comboName = null) => {
    if (!ingredient || !Array.isArray(ingredient.nutrition)) {
      console.warn(`Invalid ingredient or nutrition data:`, ingredient);
      return [];
    }
    const baseWeight = parseFloat(ingredient.weight) || 1;
    let effectiveSizeKey = sizeKey;
    if (isAddon && addonName) {
      const addonVariants = selectedAddonVariants[addonName] || {};
      effectiveSizeKey = addonVariants.size
        ? addonVariants.size === 'S' ? 'small' : addonVariants.size === 'M' ? 'medium' : 'large'
        : effectiveSizeKey;
    }
    if (isCombo && comboName) {
      const comboVariants = selectedComboVariants[comboName] || {};
      effectiveSizeKey = comboVariants.size
        ? comboVariants.size === 'S' ? 'small' : comboVariants.size === 'M' ? 'medium' : 'large'
        : effectiveSizeKey;
    }
    const sizeWeight = parseFloat(ingredient[effectiveSizeKey]) || 1;
    const scalingFactor = baseWeight !== 0 ? sizeWeight / baseWeight : 1;
    return ingredient.nutrition.map(nut => {
      if (!nut || typeof nut.nutrition_value === 'undefined') {
        console.warn(`Invalid nutrition data for ingredient:`, ingredient, nut);
        return '0.000';
      }
      const nutritionValue = parseFloat(nut.nutrition_value) || 0;
      if (nutritionValue === 0) {
        return '0.000';
      }
      const scaledValue = nutritionValue * scalingFactor;
      return Number.isFinite(scaledValue) ? scaledValue.toFixed(3) : '0.000';
    });
  };

  const getAllNutritionNames = useMemo(() => {
    const nutritionNames = new Set();
    const allIngredients = [
      ...(Array.isArray(fetchedItem?.ingredients) ? fetchedItem.ingredients : []),
      ...Object.values(addonIngredients).flat(),
      ...Object.values(comboIngredients).flat(),
    ];
    allIngredients.forEach(ingredient => {
      if (Array.isArray(ingredient.nutrition)) {
        ingredient.nutrition.forEach(nut => {
          if (nut.nutrition_name) {
            nutritionNames.add(nut.nutrition_name);
          }
        });
      }
    });
    return Array.from(nutritionNames);
  }, [fetchedItem, addonIngredients, comboIngredients]);

  const calculateTotalNutritionForSize = (sizeKey, nutritionName) => {
    let total = 0;
    const mainIngredients = Array.isArray(fetchedItem?.ingredients) ? fetchedItem.ingredients : [];
    mainIngredients.forEach(ingredient => {
      if (!Array.isArray(ingredient.nutrition)) return;
      const nutritionValues = calculateNutrition(ingredient, sizeKey);
      const nutritionIndex = ingredient.nutrition.findIndex(nut => nut.nutrition_name === nutritionName);
      if (nutritionIndex !== -1) {
        total += parseFloat(nutritionValues[nutritionIndex]) || 0;
      }
    });
    Object.entries(addonIngredients).forEach(([addonName, ingredients]) => {
      ingredients.forEach(ingredient => {
        if (!Array.isArray(ingredient.nutrition)) return;
        const nutritionValues = calculateNutrition(ingredient, sizeKey, true, addonName);
        const nutritionIndex = ingredient.nutrition.findIndex(nut => nut.nutrition_name === nutritionName);
        if (nutritionIndex !== -1) {
          const value = parseFloat(nutritionValues[nutritionIndex]) || 0;
          const qty = addonQuantities[addonName] || 0;
          total += value * qty;
        }
      });
    });
    Object.entries(comboIngredients).forEach(([comboName, ingredients]) => {
      ingredients.forEach(ingredient => {
        if (!Array.isArray(ingredient.nutrition)) return;
        const nutritionValues = calculateNutrition(ingredient, sizeKey, false, null, true, comboName);
        const nutritionIndex = ingredient.nutrition.findIndex(nut => nut.nutrition_name === nutritionName);
        if (nutritionIndex !== -1) {
          const value = parseFloat(nutritionValues[nutritionIndex]) || 0;
          const qty = comboQuantities[comboName] || 0;
          total += value * qty;
        }
      });
    });
    return Number.isFinite(total) ? total.toFixed(3) : '0.000';
  };

  const handleSizeFilterClick = size => setSelectedSizeFilter(size);
  const handleResetFilter = () => setSelectedSizeFilter(null);

  const getSelectedCustomVariantsDisplay = (variants, customVariants, type = 'item') => {
    if (!variants?.length) return 'None';
    const selected = [];
    variants.forEach(variant => {
      if (variant.enabled) {
        variant.subheadings.forEach(sub => {
          if (customVariants[sub.name]) {
            selected.push(`${variant.heading}: ${sub.name}`);
          }
        });
      }
    });
    return selected.join(', ') || 'None';
  };

  const getSelectedVariantsList = (variants, customVariants, toppings, dataItem) => {
    const parts = [];

    // Check for Size
    const sizeEnabled = dataItem.variants?.size?.enabled || dataItem.size?.enabled;
    if (sizeEnabled && variants?.size) {
      let sizeLabel = '';
      if (variants.size === 'S') sizeLabel = `Small`;
      else if (variants.size === 'M') sizeLabel = `Medium`;
      else if (variants.size === 'L') sizeLabel = `Large`;
      parts.push({ icon: 'fa-maximize', text: `Size: ${sizeLabel}` });
    }

    // Check for Cold/Ice
    const coldEnabled = dataItem.variants?.cold?.enabled || dataItem.cold?.enabled;
    if (coldEnabled && variants?.cold) {
      parts.push({
        icon: variants.cold === 'with_ice' ? 'fa-snowflake' : 'fa-gear',
        text: variants.cold === 'with_ice' ? 'With Ice' : 'Without Ice'
      });
    }

    // Check for Spicy
    const spicyEnabled = dataItem.variants?.spicy?.enabled || dataItem.spicy?.enabled;
    if (spicyEnabled && variants?.spicy !== null) {
      parts.push({
        icon: variants.spicy ? 'fa-pepper-hot' : 'fa-leaf',
        text: variants.spicy ? 'Spicy' : 'Non-Spicy'
      });
    }

    // Check for Sugar
    const sugarEnabled = dataItem.variants?.sugar?.enabled || dataItem.sugar?.enabled;
    if (sugarEnabled && variants?.sugar) {
      parts.push({
        icon: 'fa-cubes-stacked',
        text: `Sugar: ${variants.sugar.charAt(0).toUpperCase() + variants.sugar.slice(1)}`
      });
    }

    // Check for Custom Variants
    const customVariantsList = dataItem.custom_variants || [];
    if (customVariantsList.length > 0 && customVariants) {
      customVariantsList.forEach(variant => {
        if (variant.enabled) {
          variant.subheadings.forEach(sub => {
            if (customVariants[sub.name]) {
              // Only push if the sub.name is NOT the same as the addon name to avoid "manogo manogo"
              if (sub.name !== dataItem.name1 && sub.name !== dataItem.item_name) {
                parts.push({ icon: 'fa-circle-check', text: sub.name });
              }
            }
          });
        }
      });
    // Check for Toppings
    }
    if (toppings && Object.keys(toppings).length > 0) {
      Object.entries(toppings).forEach(([toppingId, isSelected]) => {
        if (isSelected) {
          const toppingDetails = allToppings.find(t => t._id === toppingId || t.topping_name === toppingId);
          if (toppingDetails) {
            parts.push({ icon: 'fa-plus', text: toppingDetails.topping_name });
          }
        }
      });
    }

    return parts;
  };

  const getSelectedVariantsSummary = (variants, customVariants, toppings, dataItem) => {
    const list = getSelectedVariantsList(variants, customVariants, toppings, dataItem);
    return list.length > 0 ? list.map(item => item.text).join(', ') : '';
  };

  if (!fetchedItem) return <div className="food-detail bg-dark"><p>Loading...</p></div>;

  const currentSizeFilter = selectedVariants.size
    ? selectedVariants.size === 'S' ? 'small' : selectedVariants.size === 'M' ? 'medium' : 'large'
    : 'medium';

  // NEW: Get selected components for kitchen notes
  const getSelectedComponents = () => {
    const components = [{ key: 'item', name: fetchedItem.item_name, type: 'Item' }];
    Object.entries(addonQuantities).forEach(([name, qty]) => {
      if (qty > 0) components.push({ key: name, name, type: 'Addon' });
    });
    Object.entries(comboQuantities).forEach(([name, qty]) => {
      if (qty > 0) components.push({ key: name, name, type: 'Combo' });
    });
    return components;
  };

  return (
    <ErrorBoundary>
      <>
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)', zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: '96vw', maxWidth: '1000px', height: '90vh', display: 'flex', flexDirection: 'column', borderRadius: '24px', overflow: 'hidden', boxShadow: '0 25px 60px rgba(0,0,0,0.4)', backgroundColor: '#fff' }}>

          {/* === STICKY HEADER === */}
          <div style={{ flexShrink: 0, background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', padding: '20px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h4 style={{ color: 'white', fontWeight: 700, margin: 0, fontSize: '22px', letterSpacing: '-0.5px' }}>{fetchedItem.item_name}</h4>
            <button type="button" onClick={handleClose} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '50%', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'white', fontSize: '18px', transition: 'background 0.2s' }}
              onMouseOver={e => e.currentTarget.style.background = 'rgba(255,255,255,0.35)'}
              onMouseOut={e => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
            >
              ×
            </button>
          </div>

          {/* === SCROLLABLE BODY === */}
          <div style={{ flex: '1 1 auto', overflowY: 'auto', overflowX: 'hidden', backgroundColor: '#f8fafc', padding: '24px' }}>

                {/* Image Section */}
                <div className="d-flex flex-column align-items-center mb-4">
                  <div className="d-flex justify-content-center align-items-center position-relative w-100" style={{ maxWidth: '300px' }}>
                    <button
                      className="image-nav-btn left-arrow position-absolute"
                      onClick={handlePrevImage}
                      disabled={!fetchedItem.images?.length}
                      style={{ left: '-20px', zIndex: 2 }}
                    >
                      <i className="fa-solid fa-chevron-left"></i>
                    </button>
                    
                    <div className="image-wrapper position-relative" style={{ width: '180px', height: '180px' }}>
                      <img
                        src={selectedPreviewImage || getImageUrl(fetchedItem.image || (fetchedItem.uploaded_images && fetchedItem.uploaded_images[0]) || (fetchedItem.images && fetchedItem.images[0]))}
                        alt={fetchedItem.item_name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%', border: '4px solid white', boxShadow: '0 12px 24px rgba(0,0,0,0.1)' }}
                        className="transition-image"
                        onError={e => (e.target.src = 'data:image/svg+xml;charset=UTF-8,%3Csvg%20width%3D%22100%22%20height%3D%22100%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Crect%20width%3D%22100%25%22%20height%3D%22100%25%22%20fill%3D%22%23eee%22%2F%3E%3Ctext%20x%3D%2250%25%22%20y%3D%2250%25%22%20font-family%3D%22Arial%22%20font-size%3D%2212%22%20fill%3D%22%23aaa%22%20dominant-baseline%3D%22middle%22%20text-anchor%3D%22middle%22%3ENo%20Image%3C%2Ftext%3E%3C%2Fsvg%3E')}
                      />
                      <div 
                        className="info-icon shadow-sm"
                        onClick={() => setShowModal(true)}
                        title="View Details"
                        style={{ position: 'absolute', top: '10px', right: '10px', background: 'white', color: '#3b82f6', width: '32px', height: '32px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', border: '2px solid #e2e8f0' }}
                      >
                        <i className="fa-solid fa-info" style={{ fontSize: '14px', fontWeight: 'bold' }}></i>
                      </div>
                    </div>

                    <button
                      className="image-nav-btn right-arrow position-absolute"
                      onClick={handleNextImage}
                      disabled={!fetchedItem.images?.length}
                      style={{ right: '-20px', zIndex: 2 }}
                    >
                      <i className="fa-solid fa-chevron-right"></i>
                    </button>
                  </div>
                  
                  {(!fetchedItem.images?.length && !fetchedItem.uploaded_images?.length) && (
                    <p className="text-center text-muted small mt-3 mb-0">No additional images available</p>
                  )}
                </div>

                {/* Details Section */}
                <div className="item-details-card bg-white p-4 rounded-4 shadow-sm mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-3 pb-3 border-bottom border-light">
                    <div>
                      <span className="text-muted small fw-semibold text-uppercase tracking-wide d-block mb-1">Category</span>
                      <span className="fw-bold text-dark" style={{ fontSize: '16px' }}>{fetchedItem.item_group || 'Unknown'}</span>
                    </div>
                    <div className="text-end">
                      <span className="text-muted small fw-semibold text-uppercase tracking-wide d-block mb-1">Base Price</span>
                      {hasActiveOffer() ? (
                        <div className="d-flex align-items-center justify-content-end gap-2">
                          <span className="text-muted text-decoration-line-through small">
                            {currencySymbol}{(fetchedItem.price_list_rate || 0).toFixed(2)}
                          </span>
                          <span className="fw-bold text-danger" style={{ fontSize: '18px' }}>
                            {currencySymbol}{calculateOfferSizePrice(fetchedItem.offer_price, selectedVariants.size).toFixed(2)}
                          </span>
                        </div>
                      ) : (
                        <span className="fw-bold text-dark" style={{ fontSize: '18px' }}>
                          {currencySymbol}{getCurrentItemPrice().toFixed(2)}
                        </span>
                      )}
                    </div>
                  </div>

                  {hasActiveOffer() && (
                    <div className="offer-timing bg-red-50 p-3 rounded-3 mb-3 border border-danger-subtle">
                      <div className="d-flex justify-content-between small">
                        <div>
                          <i className="fa-regular fa-clock text-danger me-1"></i>
                          <span className="text-danger fw-semibold">Offer Starts:</span>
                          <span className="ms-1 text-dark">{new Date(fetchedItem.offer_start_time).toLocaleString()}</span>
                        </div>
                        <div>
                          <span className="text-danger fw-semibold">Ends:</span>
                          <span className="ms-1 text-dark">{new Date(fetchedItem.offer_end_time).toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="d-flex justify-content-between align-items-center mt-2">
                    <div>
                      <span className="text-muted small fw-semibold text-uppercase tracking-wide d-block mb-2">Total Amount</span>
                      <span className="fw-bold text-primary" style={{ fontSize: '24px' }}>
                        {currencySymbol}{(totalPrice || 0).toFixed(2)}
                      </span>
                    </div>
                    
                    <div>
                      <span className="text-muted small fw-semibold text-uppercase tracking-wide d-block mb-2 text-center">Quantity</span>
                      <div className="quantity-controls bg-light rounded-pill p-1 d-flex align-items-center shadow-sm">
                        <button className="btn btn-white rounded-circle p-0 shadow-sm d-flex align-items-center justify-content-center" style={{ width: '36px', height: '36px', color: '#ef4444' }} onClick={decreaseQuantity}>
                          <i className="fa-solid fa-minus"></i>
                        </button>
                        <span className="fw-bold px-3 fs-5" style={{ minWidth: '40px', textAlign: 'center' }}>{quantity}</span>
                        <button className="btn btn-primary rounded-circle p-0 shadow-sm d-flex align-items-center justify-content-center" style={{ width: '36px', height: '36px' }} onClick={increaseQuantity}>
                          <i className="fa-solid fa-plus"></i>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="content-wrapper">
                  {(fetchedItem.variants?.size?.enabled || fetchedItem.variants?.cold?.enabled ||
                    fetchedItem.variants?.spicy?.enabled || fetchedItem.variants?.sugar?.enabled ||
                    fetchedItem.custom_variants?.some(v => v.enabled)) && (
                      <div className="mt-3">
                        {hasInteractedWithVariants && getSelectedVariantsList(selectedVariants, selectedCustomVariants, selectedItemToppings, fetchedItem).length > 0 && (
                          <div className="selected-variants-wrapper">
                            <span className="selected-variants-label">SELECTED VARIANTS</span>
                            <div className="selected-variants-container">
                              {getSelectedVariantsList(selectedVariants, selectedCustomVariants, selectedItemToppings, fetchedItem).map((part, idx) => (
                                <span key={idx} className="variant-badge">
                                  <i className={`fa-solid ${part.icon}`}></i> {part.text}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        <div className="d-flex flex-column align-items-center mt-2">
                          <div className="d-flex justify-content-center flex-wrap gap-2">
                            <button className="btn btn-outline-secondary rounded-pill px-4" onClick={() => setShowVariantPopup(true)}>
                              Select Predefined Variants
                            </button>
                            {fetchedItem.custom_variants?.some(v => v.enabled) && (
                              <button className="btn btn-outline-secondary rounded-pill px-4" onClick={() => setShowCustomVariantPopup(true)}>
                                Select Custom Variants
                              </button>
                            )}
                            {fetchedItem.toppings && fetchedItem.toppings.length > 0 && (
                              <button className="btn btn-outline-secondary rounded-pill px-4" onClick={() => setShowToppingsPopup(true)}>
                                Select Toppings
                              </button>
                            )}
                          </div>
                        </div>
                        {showToppingsPopup && (
                          <div className="modal-overlay" style={{ zIndex: 1050 }} onClick={() => setShowToppingsPopup(false)}>
                            <div className="modal-content p-4 shadow rounded" style={{ maxWidth: "340px", width: "95%", textAlign: "center", background: "#fff", border: "none" }} onClick={e => e.stopPropagation()}>
                              <h5 className="mb-4 text-dark" style={{ fontWeight: 400, fontSize: "18px" }}>Select Toppings <br/><small className="text-muted fs-6">({fetchedItem.item_name})</small></h5>
                              <div className="d-flex flex-column gap-2 mb-3">
                                {fetchedItem.toppings.map(topping => (
                                  <div key={topping._id || topping.topping_name}
                                    className="p-2 rounded border"
                                    style={{ cursor: "pointer", minWidth: "120px", transition: "all 0.2s", borderColor: selectedItemToppings[(topping._id || topping.topping_name)] ? "#28a745" : "#ced4da", backgroundColor: selectedItemToppings[(topping._id || topping.topping_name)] ? "#e8f5e9" : "#fff", borderWidth: selectedItemToppings[(topping._id || topping.topping_name)] ? "2px" : "1px" }}
                                    onClick={() => setSelectedItemToppings(prev => ({...prev, [(topping._id || topping.topping_name)]: !prev[(topping._id || topping.topping_name)]}))}>
                                    <div style={{ fontWeight: "bold", fontSize: "14px", color: "#212529" }}>
                                      {topping.topping_name} (+{currencySymbol}{topping.price || 0})
                                    </div>
                                  </div>
                                ))}
                              </div>
                              <button className="btn w-100 rounded mt-2 text-white" style={{ backgroundColor: "#6c757d", fontWeight: "500" }} onClick={() => setShowToppingsPopup(false)}>
                                Close
                              </button>
                            </div>
                          </div>
                        )}
                        {showVariantPopup && (
                          <div className="modal-overlay" style={{ zIndex: 1050 }} onClick={() => setShowVariantPopup(false)}>
                            <div className="modal-content p-4 shadow rounded" style={{ maxWidth: "340px", width: "95%", textAlign: "center", background: "#fff", border: "none" }} onClick={e => e.stopPropagation()}>
                              <h5 className="mb-4 text-dark" style={{ fontWeight: 400, fontSize: "18px" }}>Select Predefined Variants <br/><small className="text-muted fs-6">({fetchedItem.item_name})</small></h5>
                              {fetchedItem.variants?.size?.enabled && (
                                <div className="mb-3">
                                  <div className="mb-2" style={{ fontSize: "14px", color: "#333" }}>Size</div>
                                  <div className="d-flex flex-column gap-2">
                                    {[{key:"S", price: fetchedItem.variants.size.small_price}, {key:"M", price: fetchedItem.variants.size.medium_price}, {key:"L", price: fetchedItem.variants.size.large_price}].map(opt => (
                                      <div key={opt.key} className="p-2 rounded border"
                                        style={{ cursor: "pointer", minWidth: "70px", transition: "all 0.2s", borderColor: selectedVariants.size === opt.key ? "#28a745" : "#ced4da", backgroundColor: selectedVariants.size === opt.key ? "#e8f5e9" : "#fff", borderWidth: selectedVariants.size === opt.key ? "2px" : "1px" }}
                                        onClick={() => handleVariantChange("size", opt.key)}>
                                        <div style={{ fontWeight: "bold", fontSize: "14px", color: "#212529" }}>{opt.key} ({currencySymbol}{opt.price || 0})</div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {fetchedItem.variants?.cold?.enabled && (
                                <div className="mb-3">
                                  <div className="mb-2" style={{ fontSize: "14px", color: "#333" }}>Ice Preference</div>
                                  <div className="d-flex flex-column gap-2">
                                    <div className="p-2 rounded border" style={{ cursor: "pointer", minWidth: "100px", transition: "all 0.2s", borderColor: selectedVariants.cold === "without_ice" ? "#28a745" : "#ced4da", backgroundColor: selectedVariants.cold === "without_ice" ? "#e8f5e9" : "#fff", borderWidth: selectedVariants.cold === "without_ice" ? "2px" : "1px" }} onClick={() => handleVariantChange("cold", "without_ice")}>
                                      <div style={{ fontWeight: "bold", fontSize: "14px", color: "#212529" }}>Without Ice</div>
                                    </div>
                                    <div className="p-2 rounded border" style={{ cursor: "pointer", minWidth: "100px", transition: "all 0.2s", borderColor: selectedVariants.cold === "with_ice" ? "#28a745" : "#ced4da", backgroundColor: selectedVariants.cold === "with_ice" ? "#e8f5e9" : "#fff", borderWidth: selectedVariants.cold === "with_ice" ? "2px" : "1px" }} onClick={() => handleVariantChange("cold", "with_ice")}>
                                      <div style={{ fontWeight: "bold", fontSize: "14px", color: "#212529" }}>With Ice (+{currencySymbol}{fetchedItem.variants.cold.ice_price || 0})</div>
                                    </div>
                                  </div>
                                </div>
                              )}
                              {fetchedItem.variants?.spicy?.enabled && (
                                <div className="mb-3">
                                  <div className="mb-2" style={{ fontSize: "14px", color: "#333" }}>Spicy Preference</div>
                                  <div className="d-flex flex-column gap-2">
                                    <div className="p-2 rounded border" style={{ cursor: "pointer", minWidth: "100px", transition: "all 0.2s", borderColor: !selectedVariants.spicy ? "#28a745" : "#ced4da", backgroundColor: !selectedVariants.spicy ? "#e8f5e9" : "#fff", borderWidth: !selectedVariants.spicy ? "2px" : "1px" }} onClick={() => handleVariantChange("spicy", false)}>
                                      <div style={{ fontWeight: "bold", fontSize: "14px", color: "#212529" }}>Non-Spicy ({currencySymbol}{fetchedItem.variants.spicy.non_spicy_price || 0})</div>
                                    </div>
                                    <div className="p-2 rounded border" style={{ cursor: "pointer", minWidth: "100px", transition: "all 0.2s", borderColor: selectedVariants.spicy ? "#28a745" : "#ced4da", backgroundColor: selectedVariants.spicy ? "#e8f5e9" : "#fff", borderWidth: selectedVariants.spicy ? "2px" : "1px" }} onClick={() => handleVariantChange("spicy", true)}>
                                      <div style={{ fontWeight: "bold", fontSize: "14px", color: "#212529" }}>Spicy (+{currencySymbol}{fetchedItem.variants.spicy.spicy_price || 30})</div>
                                    </div>
                                  </div>
                                </div>
                              )}
                              {fetchedItem.variants?.sugar?.enabled && (
                                <div className="mb-3">
                                  <div className="mb-2" style={{ fontSize: "14px", color: "#333" }}>Sugar Level</div>
                                  <div className="d-flex flex-column gap-2">
                                    {[{key:"less", label:"Less"}, {key:"medium", label:"Medium"}, {key:"extra", label:"Extra"}].map(opt => (
                                      <div key={opt.key} className="p-2 rounded border" style={{ cursor: "pointer", minWidth: "70px", transition: "all 0.2s", borderColor: selectedVariants.sugar === opt.key ? "#28a745" : "#ced4da", backgroundColor: selectedVariants.sugar === opt.key ? "#e8f5e9" : "#fff", borderWidth: selectedVariants.sugar === opt.key ? "2px" : "1px" }} onClick={() => handleVariantChange("sugar", opt.key)}>
                                        <div style={{ fontWeight: "bold", fontSize: "14px", color: "#212529" }}>{opt.label}</div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                              <button className="btn w-100 rounded mt-2 text-white" style={{ backgroundColor: "#6c757d", fontWeight: "500" }} onClick={() => setShowVariantPopup(false)}>
                                Close
                              </button>
                            </div>
                          </div>
                        )}
                        {showCustomVariantPopup && (
                          <div className="modal-overlay" style={{ zIndex: 1050 }} onClick={() => setShowCustomVariantPopup(false)}>
                            <div className="modal-content p-4 shadow rounded" style={{ maxWidth: "340px", width: "95%", textAlign: "center", background: "#fff", border: "none", maxHeight: "85vh", overflowY: "auto" }} onClick={e => e.stopPropagation()}>
                              <h5 className="mb-4 text-dark" style={{ fontWeight: 400, fontSize: "18px" }}>Select Custom Variants <br/><small className="text-muted fs-6">({fetchedItem.item_name})</small></h5>
                              {fetchedItem.custom_variants?.map(variant => variant.enabled && (
                                <div key={variant._id} className="mb-3">
                                  <div className="mb-2" style={{ fontSize: "14px", color: "#333" }}>{variant.heading}</div>
                                  <div className="d-flex flex-column gap-2">
                                    {variant.subheadings.map(sub => (
                                      <div key={sub.name} className="p-2 rounded border"
                                        style={{ cursor: "pointer", minWidth: "120px", transition: "all 0.2s", borderColor: selectedCustomVariants[sub.name] ? "#28a745" : "#ced4da", backgroundColor: selectedCustomVariants[sub.name] ? "#e8f5e9" : "#fff", borderWidth: selectedCustomVariants[sub.name] ? "2px" : "1px" }}
                                        onClick={() => handleCustomVariantChange(sub.name)}>
                                        <div style={{ fontWeight: "bold", fontSize: "14px", color: "#212529" }}>{sub.name} (+{currencySymbol}{sub.price || 0})</div>
                                        {sub.image && variant.activeSection === "priceAndImage" && (
                                          <img src={getImageUrl(sub.image)} alt={sub.name} width={50} height={50} className="mt-1 rounded" onError={e => (e.target.src = "data:image/svg+xml;charset=UTF-8,%3Csvg%20width%3D%22100%22%20height%3D%22100%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Crect%20width%3D%22100%25%22%20height%3D%22100%25%22%20fill%3D%22%23eee%22%2F%3E%3Ctext%20x%3D%2250%25%22%20y%3D%2250%25%22%20font-family%3D%22Arial%22%20font-size%3D%2212%22%20fill%3D%22%23aaa%22%20dominant-baseline%3D%22middle%22%20text-anchor%3D%22middle%22%3ENo%20Image%3C%2Ftext%3E%3C%2Fsvg%3E")} />
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ))}
                              <button className="btn w-100 rounded mt-2 text-white" style={{ backgroundColor: "#6c757d", fontWeight: "500" }} onClick={() => setShowCustomVariantPopup(false)}>
                                Close
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  {fetchedItem.addons?.length > 0 && (
                    <div className="mt-4 pt-3 border-top border-light">
                      <strong className="d-block mb-3 text-dark fw-bold" style={{ fontSize: '18px' }}>Add-ons</strong>
                      <ul className="addons-list row g-3 p-0 m-0" style={{ listStyle: 'none' }}>
                        {fetchedItem.addons.map(addon => {
                          const isSelected = addonQuantities[addon.name1] > 0;
                          const addonQty = addonQuantities[addon.name1] || 0;
                          const currentPrice = getCurrentAddonPrice(addon);
                          return (
                            <li key={addon.name1} className="addon-container col-6 d-flex">
                              <div
                                className={`addon-item card w-100 d-flex flex-column position-relative transition-all border-0 shadow-sm ${isSelected ? 'selected' : ''}`} 
                                tabIndex="0"
                                onClick={() => handleAddonCheck(addon, !isSelected)}
                                style={{ 
                                  cursor: 'pointer',
                                  borderRadius: '16px',
                                  backgroundColor: isSelected ? '#eff6ff' : '#ffffff',
                                  outline: isSelected ? '2px solid #3b82f6' : '1px solid #e2e8f0',
                                  transform: isSelected ? 'translateY(-2px)' : 'none',
                                  boxShadow: isSelected ? '0 10px 20px rgba(59, 130, 246, 0.1)' : '0 4px 6px rgba(0,0,0,0.02)'
                                }}
                              >
                                {isSelected && (
                                  <div className="position-absolute bg-primary text-white rounded-circle d-flex align-items-center justify-content-center shadow-sm" style={{ top: '-8px', right: '-8px', width: '24px', height: '24px', zIndex: 5 }}>
                                    <i className="fa-solid fa-check" style={{ fontSize: '12px' }}></i>
                                  </div>
                                )}
                                <div className="p-3 text-center d-flex flex-column align-items-center flex-grow-1">
                                  <img
                                    src={getImageUrl(addon.addon_image || addon.image)}
                                    style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '50%', marginBottom: '12px', border: '3px solid white', boxShadow: '0 4px 10px rgba(0,0,0,0.08)' }}
                                    alt={addon.name1}
                                    onError={e => (e.target.src = 'data:image/svg+xml;charset=UTF-8,%3Csvg%20width%3D%22100%22%20height%3D%22100%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Crect%20width%3D%22100%25%22%20height%3D%22100%25%22%20fill%3D%22%23eee%22%2F%3E%3Ctext%20x%3D%2250%25%22%20y%3D%2250%25%22%20font-family%3D%22Arial%22%20font-size%3D%2212%22%20fill%3D%22%23aaa%22%20dominant-baseline%3D%22middle%22%20text-anchor%3D%22middle%22%3ENo%20Image%3C%2Ftext%3E%3C%2Fsvg%3E')}
                                  />
                                  <span className="fw-semibold text-dark w-100 mb-1 lh-sm" style={{ fontSize: '14px' }}>{addon.name1}</span>
                                  <span className="text-primary fw-bold mt-auto pt-2" style={{ fontSize: '13px' }}>{currencySymbol}{(currentPrice || 0).toFixed(2)}</span>
                                </div>
                                 {isSelected && (
                                   <div className="p-2 border-top border-light mt-auto w-100 bg-white" style={{ borderBottomLeftRadius: "16px", borderBottomRightRadius: "16px" }}>
                                      <div className="d-flex flex-column align-items-center gap-2 w-100 mt-2 px-1">
                                        {(addon.size?.enabled || addon.cold?.enabled || addon.spicy?.enabled || addon.sugar?.enabled) && (
                                          <button className="btn btn-outline-secondary btn-sm w-100 rounded-pill" onClick={e => { e.stopPropagation(); setShowAddonVariantPopup(addon.name1); }} style={{ fontSize: "12.5px", padding: "6px 12px" }}>
                                            Select Predefined Variants
                                          </button>
                                        )}
                                        {addon.custom_variants?.some(v => v.enabled) && (
                                          <button className="btn btn-outline-secondary btn-sm w-100 rounded-pill" onClick={e => { e.stopPropagation(); setShowAddonCustomVariantPopup(addon.name1); }} style={{ fontSize: "12.5px", padding: "6px 12px" }}>
                                            Select Custom Variants
                                          </button>
                                        )}
                                        {addon.toppings?.length > 0 && (
                                          <button className="btn btn-outline-secondary btn-sm w-100 rounded-pill" onClick={e => { e.stopPropagation(); setShowAddonToppingsPopup(addon.name1); }} style={{ fontSize: "12.5px", padding: "6px 12px" }}>
                                            Select Toppings
                                          </button>
                                        )}
                                      </div>

                                  {getSelectedVariantsList(selectedAddonVariants[addon.name1], selectedAddonCustomVariants[addon.name1], selectedAddonToppings[addon.name1], addon).length > 0 && (
                                    <div className="selected-variants-wrapper mt-2 p-2" style={{ maxWidth: "95%", margin: "4px auto", borderRadius: "8px", border: "1px dotted #c1e1c5" }}>
                                      <span className="selected-variants-label" style={{ fontSize: "10px", marginBottom: "4px" }}>SELECTED</span>
                                      <div className="selected-variants-container" style={{ gap: "4px" }}>
                                        {getSelectedVariantsList(selectedAddonVariants[addon.name1], selectedAddonCustomVariants[addon.name1], selectedAddonToppings[addon.name1], addon).map((part, idx) => (
                                          <span key={idx} className="variant-badge" style={{ padding: "2px 6px", fontSize: "10px" }}>
                                            <i className={`fa-solid ${part.icon}`} style={{ fontSize: "10px" }}></i> {part.text}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  <div className="quantity-container mt-2 d-flex justify-content-center align-items-center bg-light rounded-pill p-1 mx-auto" style={{ width: 'max-content' }}>
                                    <button
                                      className="btn btn-white rounded-circle p-0 d-flex justify-content-center align-items-center"
                                      style={{ width: '28px', height: '28px', color: '#ef4444' }}
                                      onClick={e => {
                                        e.stopPropagation();
                                        decreaseAddonQuantity(addon.name1);
                                      }}
                                    >
                                      <i className="fa-solid fa-minus fs-6"></i>
                                    </button>
                                    <span className="fw-bold px-3" style={{ fontSize: '14px' }}>{addonQty}</span>
                                    <button
                                      className="btn btn-primary rounded-circle p-0 d-flex justify-content-center align-items-center shadow-sm"
                                      style={{ width: '28px', height: '28px' }}
                                      onClick={e => {
                                        e.stopPropagation();
                                        increaseAddonQuantity(addon.name1);
                                      }}
                                    >
                                      <i className="fa-solid fa-plus fs-6 text-white"></i>
                                    </button>
                                  </div>
                                </div>
                              )}
                              </div>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                  {fetchedItem.combos?.length > 0 && (item.isCombo || fetchedItem.type === 'item_combo' || fetchedItem.type === 'combo_offer' || (item.combos && item.combos.length > 0)) && (
                    <div className="mt-4 pt-3 border-top border-light">
                      <strong className="d-block mb-3 text-dark fw-bold" style={{ fontSize: '18px' }}>Combos</strong>
                      <div className="combo-list">
                          <ul className="addons-list row g-3 p-0 m-0" style={{ listStyle: 'none' }}>
                            {fetchedItem.combos.map(combo => {
                              const isSelected = comboQuantities[combo.name1] > 0;
                              const comboQty = comboQuantities[combo.name1] || 0;
                              const currentPrice = getCurrentComboPrice(combo);
                              return (
                                <li key={combo.name1} className="addon-container col-6 d-flex">
                                  <div
                                    className={`addon-item card w-100 d-flex flex-column position-relative transition-all border-0 shadow-sm ${isSelected ? 'selected' : ''}`} 
                                    tabIndex="0"
                                    onClick={() => handleComboCheck(combo)}
                                    style={{ 
                                      cursor: 'pointer',
                                      borderRadius: '16px',
                                      backgroundColor: isSelected ? '#fdf4ff' : '#ffffff',
                                      outline: isSelected ? '2px solid #d946ef' : '1px solid #e2e8f0',
                                      transform: isSelected ? 'translateY(-2px)' : 'none',
                                      boxShadow: isSelected ? '0 10px 20px rgba(217, 70, 239, 0.1)' : '0 4px 6px rgba(0,0,0,0.02)'
                                    }}
                                  >
                                    {isSelected && (
                                      <div className="position-absolute bg-purple text-white rounded-circle d-flex align-items-center justify-content-center shadow-sm" style={{ top: '-8px', right: '-8px', width: '24px', height: '24px', zIndex: 5, backgroundColor: '#d946ef' }}>
                                        <i className="fa-solid fa-check" style={{ fontSize: '12px' }}></i>
                                      </div>
                                    )}
                                    <div className="p-3 text-center d-flex flex-column align-items-center flex-grow-1">
                                      <img
                                        src={getImageUrl(combo.combo_image || combo.image)}
                                        style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '50%', marginBottom: '12px', border: '3px solid white', boxShadow: '0 4px 10px rgba(0,0,0,0.08)' }}
                                        alt={combo.name1}
                                        onError={e => (e.target.src = 'data:image/svg+xml;charset=UTF-8,%3Csvg%20width%3D%22100%22%20height%3D%22100%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Crect%20width%3D%22100%25%22%20height%3D%22100%25%22%20fill%3D%22%23eee%22%2F%3E%3Ctext%20x%3D%2250%25%22%20y%3D%2250%25%22%20font-family%3D%22Arial%22%20font-size%3D%2212%22%20fill%3D%22%23aaa%22%20dominant-baseline%3D%22middle%22%20text-anchor%3D%22middle%22%3ENo%20Image%3C%2Ftext%3E%3C%2Fsvg%3E')}
                                      />
                                      <span className="fw-semibold text-dark w-100 mb-1 lh-sm" style={{ fontSize: '14px' }}>{combo.name1}</span>
                                      <span className="text-purple fw-bold mt-auto pt-2" style={{ fontSize: '13px', color: '#d946ef' }}>{currencySymbol}{(currentPrice || 0).toFixed(2)}</span>
                                    </div>
                                     {isSelected && (
                                       <div className="p-2 border-top border-light mt-auto w-100 bg-white" style={{ borderBottomLeftRadius: "16px", borderBottomRightRadius: "16px" }}>
                                      <div className="d-flex flex-column align-items-center gap-2 w-100 mt-2 px-1">
                                        {(combo.size?.enabled || combo.cold?.enabled || combo.spicy?.enabled || combo.sugar?.enabled) && (
                                          <button className="btn btn-outline-secondary btn-sm w-100 rounded-pill" onClick={e => { e.stopPropagation(); setShowComboVariantPopup(combo.name1); }} style={{ fontSize: "12.5px", padding: "6px 12px" }}>
                                            Select Predefined Variants
                                          </button>
                                        )}
                                        {combo.custom_variants?.some(v => v.enabled) && (
                                          <button className="btn btn-outline-secondary btn-sm w-100 rounded-pill" onClick={e => { e.stopPropagation(); setShowComboCustomVariantPopup(combo.name1); }} style={{ fontSize: "12.5px", padding: "6px 12px" }}>
                                            Select Custom Variants
                                          </button>
                                        )}
                                        {combo.toppings?.length > 0 && (
                                          <button className="btn btn-outline-secondary btn-sm w-100 rounded-pill" onClick={e => { e.stopPropagation(); setShowComboToppingsPopup(combo.name1); }} style={{ fontSize: "12.5px", padding: "6px 12px" }}>
                                            Select Toppings
                                          </button>
                                        )}
                                      </div>

                                      </div>
                                    )}
                                     {isSelected && (
                                        <div>
                                       {getSelectedVariantsList(selectedComboVariants[combo.name1], selectedComboCustomVariants[combo.name1], selectedComboToppings[combo.name1], combo).length > 0 && (
                                         <div className="selected-variants-wrapper mt-2 p-2" style={{ maxWidth: "95%", margin: "4px auto", borderRadius: "8px", border: "1px dotted #c1e1c5" }}>
                                           <span className="selected-variants-label" style={{ fontSize: "10px", marginBottom: "4px" }}>SELECTED</span>
                                           <div className="selected-variants-container" style={{ gap: "4px" }}>
                                             {getSelectedVariantsList(selectedComboVariants[combo.name1], selectedComboCustomVariants[combo.name1], selectedComboToppings[combo.name1], combo).map((part, idx) => (
                                               <span key={idx} className="variant-badge" style={{ padding: "2px 6px", fontSize: "10px" }}>
                                                 <i className={`fa-solid ${part.icon}`} style={{ fontSize: "10px" }}></i> {part.text}
                                               </span>
                                             ))}
                                           </div>
                                         </div>
                                       )}
                                       
                                       <div className="quantity-container mt-2 d-flex justify-content-center align-items-center bg-light rounded-pill p-1 mx-auto" style={{ width: "max-content" }}>
                                         <button
                                           className="btn btn-white rounded-circle p-0 d-flex justify-content-center align-items-center"
                                           style={{ width: "28px", height: "28px", color: "#ef4444" }}
                                           onClick={e => {
                                             e.stopPropagation();
                                             decreaseComboQuantity(combo.name1);
                                           }}
                                         >
                                           <i className="fa-solid fa-minus fs-6"></i>
                                         </button>
                                         <span className="fw-bold px-3" style={{ fontSize: "14px" }}>{comboQty}</span>
                                         <button
                                           className="btn rounded-circle p-0 d-flex justify-content-center align-items-center shadow-sm text-white"
                                           style={{ width: "28px", height: "28px", backgroundColor: "#d946ef" }}
                                           onClick={e => {
                                             e.stopPropagation();
                                             increaseComboQuantity(combo.name1);
                                           }}
                                         >
                                           <i className="fa-solid fa-plus fs-6"></i>
                                         </button>
                                       </div>
                                       </div>
                                     )}
                                   </div>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                    </div>
                  )}
                </div>
            </div>

          {/* === STICKY FOOTER === */}
          <div style={{ flexShrink: 0, backgroundColor: '#ffffff', padding: '16px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
            <button 
              type="button" 
              style={{ background: 'none', border: '1.5px solid #06b6d4', color: '#06b6d4', borderRadius: '50px', padding: '8px 20px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px', transition: 'all 0.2s' }}
              onClick={() => setShowKitchenNoteModal(true)}
              onMouseOver={e => { e.currentTarget.style.background = '#06b6d4'; e.currentTarget.style.color = 'white'; }}
              onMouseOut={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = '#06b6d4'; }}
            >
              <i className="fa-regular fa-note-sticky"></i> Kitchen Note
            </button>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button 
                type="button" 
                style={{ background: '#f1f5f9', border: 'none', borderRadius: '50px', padding: '10px 24px', fontWeight: 600, color: '#64748b', cursor: 'pointer', fontSize: '14px', transition: 'all 0.2s' }}
                onClick={handleClose}
                onMouseOver={e => e.currentTarget.style.background = '#e2e8f0'}
                onMouseOut={e => e.currentTarget.style.background = '#f1f5f9'}
              >
                Cancel
              </button>
              <button 
                type="button" 
                style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', border: 'none', borderRadius: '50px', padding: '10px 28px', fontWeight: 700, color: 'white', cursor: 'pointer', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 4px 12px rgba(59,130,246,0.4)', transition: 'all 0.2s' }}
                onClick={handleAddToCart}
                onMouseOver={e => e.currentTarget.style.transform = 'translateY(-1px)'}
                onMouseOut={e => e.currentTarget.style.transform = 'none'}
              >
                <i className="fa-solid fa-cart-plus"></i> {cartItem ? 'Update Cart' : 'Add to Cart'}
              </button>
            </div>
          </div>

        </div>
      </div>
        {showKitchenNoteModal && (
          <div className="modal fade show d-block sec-modal" style={{ zIndex: 1060 }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Kitchen Notes</h5>
                  <button type="button" className="btn-close" onClick={() => setShowKitchenNoteModal(false)}></button>
                </div>
                <div className="modal-body" style={{ maxHeight: '50vh', overflowY: 'auto' }}>
                  {getSelectedComponents().map((component) => (
                    <div key={component.key} className="mb-3 p-2 border rounded">
                      <h6>{component.type}: {component.name}</h6>
                      <textarea
                        className="form-control"
                        rows="2"
                        placeholder={`Enter note for ${component.name}...`}
                        value={kitchenNotes[component.key] || ''}
                        onChange={(e) => handleKitchenNoteChange(component.key, e.target.value)}
                      />
                    </div>
                  ))}
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowKitchenNoteModal(false)}>
                    Close
                  </button>
                  <button type="button" className="btn btn-primary" onClick={saveKitchenNotes}>
                    Save Notes
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        {showModal && (
          <div className="modal-side-content" style={{ zIndex: 1060 }}>
            <div className="modal-side-header">
              <h5 className="modal-side-title">{fetchedItem.item_name} Details</h5>
              <i className="fa-solid fa-times close-icon" onClick={() => setShowModal(false)}></i>
            </div>
            <div className="modal-side-body">
              <div className="ingredients-section" style={{
                background: '#ffffff',
                borderRadius: '16px',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.05)',
                border: '1px solid #eef2f6',
                padding: '24px',
                marginTop: '24px',
                overflow: 'hidden',
                fontFamily: "'Inter', 'Segoe UI', sans-serif"
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '2px solid #f8fafc', paddingBottom: '16px' }}>
                  <h6 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <i className="fa-solid fa-leaf" style={{ color: '#10b981', fontSize: '16px' }}></i> Ingredients & Nutrition
                  </h6>
                  <div className="size-filter-buttons" style={{ display: 'flex', gap: '8px' }}>
                    {fetchedItem.variants?.size?.enabled && (
                      <>
                        <button
                          style={{
                            padding: '6px 14px', borderRadius: '20px', fontSize: '13px', fontWeight: '600', border: 'none', transition: 'all 0.3s ease', cursor: 'pointer',
                            background: selectedSizeFilter === currentSizeFilter ? 'linear-gradient(135deg, #3b82f6, #2563eb)' : '#f1f5f9',
                            color: selectedSizeFilter === currentSizeFilter ? '#fff' : '#64748b',
                            boxShadow: selectedSizeFilter === currentSizeFilter ? '0 4px 12px rgba(59, 130, 246, 0.3)' : 'none'
                          }}
                          onClick={() => handleSizeFilterClick(currentSizeFilter)}
                        >
                          {currentSizeFilter.charAt(0).toUpperCase() + currentSizeFilter.slice(1)} Size
                        </button>
                        <button
                          style={{
                            padding: '6px 14px', borderRadius: '20px', fontSize: '13px', fontWeight: '600', border: 'none', transition: 'all 0.3s ease', cursor: 'pointer',
                            background: !selectedSizeFilter ? 'linear-gradient(135deg, #10b981, #059669)' : '#f1f5f9',
                            color: !selectedSizeFilter ? '#fff' : '#64748b',
                            boxShadow: !selectedSizeFilter ? '0 4px 12px rgba(16, 185, 129, 0.3)' : 'none'
                          }}
                          onClick={handleResetFilter}
                        >
                          Show All
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {fetchedItem.ingredients.length === 0 &&
                  Object.values(addonIngredients).every(ings => ings.length === 0) &&
                  Object.values(comboIngredients).every(ings => ings.length === 0) ? (
                  <div style={{ padding: '30px', textAlign: 'center', background: '#f8fafc', borderRadius: '12px' }}>
                    <i className="fa-solid fa-box-open" style={{ fontSize: '30px', color: '#cbd5e1', marginBottom: '10px' }}></i>
                    <p style={{ margin: 0, color: '#64748b', fontWeight: '500' }}>No ingredients or nutrition data available.</p>
                  </div>
                ) : (
                  <div style={{ overflowX: 'auto', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 4px 15px rgba(0, 0, 0, 0.02)' }}>
                    {selectedSizeFilter ? (
                      <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: '600px' }}>
                        <thead>
                          <tr style={{ background: '#f8fafc' }}>
                            <th style={{ padding: '14px 16px', fontSize: '13px', fontWeight: '700', color: '#334155', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e2e8f0' }}>Nutrition</th>
                            {fetchedItem.ingredients.map((ingredient, index) => (
                              <th key={`main-${index}`} style={{ padding: '14px 16px', fontSize: '13px', fontWeight: '700', color: '#0ea5e9', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e2e8f0' }}>{ingredient.name}</th>
                            ))}
                            {Object.entries(addonIngredients).map(([addonName, ingredients]) =>
                              ingredients.map((ingredient, index) => (
                                <th key={`addon-${addonName}-${index}`} style={{ padding: '14px 16px', fontSize: '13px', fontWeight: '700', color: '#8b5cf6', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e2e8f0' }}>
                                  {ingredient.name} <span style={{ fontSize: '10px', background: '#ede9fe', padding: '2px 6px', borderRadius: '4px', verticalAlign: 'middle', marginLeft: '4px' }}>{addonName}</span>
                                </th>
                              ))
                            )}
                            {Object.entries(comboIngredients).map(([comboName, ingredients]) =>
                              ingredients.map((ingredient, index) => (
                                <th key={`combo-${comboName}-${index}`} style={{ padding: '14px 16px', fontSize: '13px', fontWeight: '700', color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e2e8f0' }}>
                                  {ingredient.name} <span style={{ fontSize: '10px', background: '#fef3c7', padding: '2px 6px', borderRadius: '4px', verticalAlign: 'middle', marginLeft: '4px' }}>{comboName}</span>
                                </th>
                              ))
                            )}
                            <th style={{ padding: '14px 16px', fontSize: '13px', fontWeight: '700', color: '#10b981', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e2e8f0' }}>Total</th>
                          </tr>
                          <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '1px solid #e2e8f0' }}>
                            <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: '600', color: '#475569' }}>Portion Size (gm)</td>
                            {fetchedItem.ingredients.map((ingredient, ingIndex) => (
                              <td key={`main-size-${ingIndex}`} style={{ padding: '12px 16px', fontSize: '13px', fontWeight: '600', color: '#0284c7' }}>
                                {ingredient[selectedSizeFilter] || 0} gm
                              </td>
                            ))}
                            {Object.entries(addonIngredients).map(([addonName, ingredients]) =>
                              ingredients.map((ingredient, ingIndex) => (
                                <td key={`addon-size-${addonName}-${ingIndex}`} style={{ padding: '12px 16px', fontSize: '13px', fontWeight: '600', color: '#7c3aed' }}>
                                  {ingredient[selectedSizeFilter] || 0} gm
                                </td>
                              ))
                            )}
                            {Object.entries(comboIngredients).map(([comboName, ingredients]) =>
                              ingredients.map((ingredient, ingIndex) => (
                                <td key={`combo-size-${comboName}-${ingIndex}`} style={{ padding: '12px 16px', fontSize: '13px', fontWeight: '600', color: '#d97706' }}>
                                  {ingredient[selectedSizeFilter] || 0} gm
                                </td>
                              ))
                            )}
                            <td style={{ padding: '12px 16px', fontSize: '13px', fontWeight: '700', color: '#059669' }}>-</td>
                          </tr>
                        </thead>
                        <tbody>
                          {getAllNutritionNames.map((nutritionName, nutIndex) => {
                            const hasNutrition = [
                              ...(Array.isArray(fetchedItem?.ingredients) ? fetchedItem.ingredients : []),
                              ...Object.values(addonIngredients).flat(),
                              ...Object.values(comboIngredients).flat(),
                            ].some(ing => ing.nutrition?.some(nut => nut.nutrition_name === nutritionName));
                            if (!hasNutrition) return null;
                            
                            const isEven = nutIndex % 2 === 0;
                            return (
                              <tr key={nutIndex} style={{ backgroundColor: isEven ? '#ffffff' : '#f8fafc', transition: 'background 0.2s ease' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f1f5f9'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = isEven ? '#ffffff' : '#f8fafc'}>
                                <td style={{ padding: '12px 16px', fontSize: '14px', fontWeight: '600', color: '#334155', borderBottom: '1px solid #f1f5f9' }}>{nutritionName}</td>
                                {fetchedItem.ingredients.map((ingredient, ingIndex) => {
                                  const nutritionIndex = ingredient.nutrition?.findIndex(nut => nut.nutrition_name === nutritionName) ?? -1;
                                  if (nutritionIndex === -1) return <td key={`main-${ingIndex}`} style={{ padding: '12px 16px', color: '#94a3b8', borderBottom: '1px solid #f1f5f9' }}>-</td>;
                                  const scaledValues = calculateNutrition(ingredient, selectedSizeFilter);
                                  return (
                                    <td key={`main-${ingIndex}`} style={{ padding: '12px 16px', fontSize: '14px', color: '#475569', borderBottom: '1px solid #f1f5f9' }}>
                                      {scaledValues[nutritionIndex]} gm
                                    </td>
                                  );
                                })}
                                {Object.entries(addonIngredients).map(([addonName, ingredients]) =>
                                  ingredients.map((ingredient, ingIndex) => {
                                    const nutritionIndex = ingredient.nutrition?.findIndex(nut => nut.nutrition_name === nutritionName) ?? -1;
                                    if (nutritionIndex === -1) return <td key={`addon-${addonName}-${ingIndex}`} style={{ padding: '12px 16px', color: '#94a3b8', borderBottom: '1px solid #f1f5f9' }}>-</td>;
                                    const scaledValues = calculateNutrition(ingredient, selectedSizeFilter, true, addonName);
                                    return (
                                      <td key={`addon-${addonName}-${ingIndex}`} style={{ padding: '12px 16px', fontSize: '14px', color: '#475569', borderBottom: '1px solid #f1f5f9' }}>
                                        {scaledValues[nutritionIndex]} gm
                                      </td>
                                    );
                                  })
                                )}
                                {Object.entries(comboIngredients).map(([comboName, ingredients]) =>
                                  ingredients.map((ingredient, ingIndex) => {
                                    const nutritionIndex = ingredient.nutrition?.findIndex(nut => nut.nutrition_name === nutritionName) ?? -1;
                                    if (nutritionIndex === -1) return <td key={`combo-${comboName}-${ingIndex}`} style={{ padding: '12px 16px', color: '#94a3b8', borderBottom: '1px solid #f1f5f9' }}>-</td>;
                                    const scaledValues = calculateNutrition(ingredient, selectedSizeFilter, false, null, true, comboName);
                                    return (
                                      <td key={`combo-${comboName}-${ingIndex}`} style={{ padding: '12px 16px', fontSize: '14px', color: '#475569', borderBottom: '1px solid #f1f5f9' }}>
                                        {scaledValues[nutritionIndex]} gm
                                      </td>
                                    );
                                  })
                                )}
                                <td style={{ padding: '12px 16px', fontSize: '14px', fontWeight: '700', color: '#059669', borderBottom: '1px solid #f1f5f9', background: '#f0fdf4' }}>
                                  {calculateTotalNutritionForSize(selectedSizeFilter, nutritionName)} gm
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    ) : (
                      <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', minWidth: '700px' }}>
                        <thead>
                          <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                            <th style={{ padding: '16px', fontSize: '13px', fontWeight: '700', color: '#334155', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Ingredient</th>
                            <th style={{ padding: '16px', fontSize: '13px', fontWeight: '700', color: '#334155', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Nutrition</th>
                            <th style={{ padding: '16px', fontSize: '13px', fontWeight: '700', color: '#334155', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'center' }}>Small</th>
                            <th style={{ padding: '16px', fontSize: '13px', fontWeight: '700', color: '#334155', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'center' }}>Medium</th>
                            <th style={{ padding: '16px', fontSize: '13px', fontWeight: '700', color: '#334155', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'center' }}>Large</th>
                            <th style={{ padding: '16px', fontSize: '13px', fontWeight: '700', color: '#10b981', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'center' }}>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {fetchedItem.ingredients.map((ingredient, ingIndex) => {
                            if (!ingredient.nutrition || ingredient.nutrition.length === 0) {
                              return (
                                <tr key={`main-${ingIndex}-no-nut`} style={{ borderBottom: '2px solid #f1f5f9' }}>
                                  <td style={{ padding: '16px', borderRight: '1px solid #f1f5f9', verticalAlign: 'top' }}>
                                    <div style={{ fontWeight: '700', color: '#0ea5e9', fontSize: '15px' }}>{ingredient.name}</div>
                                    <div style={{ fontSize: '11px', color: '#64748b', marginTop: '6px', background: '#f8fafc', display: 'inline-block', padding: '3px 8px', borderRadius: '12px' }}>Base: {ingredient.weight || 0} gm</div>
                                  </td>
                                  <td style={{ padding: '16px', color: '#94a3b8' }}>-</td>
                                  <td style={{ padding: '16px', textAlign: 'center' }}><div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px' }}>{ingredient.small || 0} gm</div><span style={{ color: '#cbd5e1' }}>-</span></td>
                                  <td style={{ padding: '16px', textAlign: 'center' }}><div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px' }}>{ingredient.medium || 0} gm</div><span style={{ color: '#cbd5e1' }}>-</span></td>
                                  <td style={{ padding: '16px', textAlign: 'center' }}><div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px' }}>{ingredient.large || 0} gm</div><span style={{ color: '#cbd5e1' }}>-</span></td>
                                  <td style={{ padding: '16px', textAlign: 'center', background: '#f8fafc', color: '#94a3b8' }}>-</td>
                                </tr>
                              );
                            }
                            return ingredient.nutrition.map((nut, nutIndex) => (
                              <tr key={`main-${ingIndex}-${nutIndex}`} style={{ borderBottom: nutIndex === ingredient.nutrition.length - 1 ? '2px solid #e2e8f0' : '1px solid #f1f5f9' }}>
                                {nutIndex === 0 && (
                                  <td rowSpan={ingredient.nutrition.length} style={{ padding: '16px', borderRight: '1px solid #f1f5f9', verticalAlign: 'top', background: '#fafcff' }}>
                                    <div style={{ fontWeight: '700', color: '#0ea5e9', fontSize: '15px' }}>{ingredient.name}</div>
                                    <div style={{ fontSize: '11px', color: '#64748b', marginTop: '6px', background: '#f1f5f9', display: 'inline-block', padding: '3px 8px', borderRadius: '12px' }}>Base: {ingredient.weight || 0} gm</div>
                                  </td>
                                )}
                                <td style={{ padding: '12px 16px', fontSize: '14px', fontWeight: '600', color: '#475569' }}>{nut.nutrition_name}</td>
                                <td style={{ padding: '12px 16px', textAlign: 'center', fontSize: '14px', color: '#64748b' }}>
                                  {nutIndex === 0 && <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{ingredient.small || 0} gm</div>}
                                  {calculateNutrition(ingredient, 'small')[nutIndex]} gm
                                </td>
                                <td style={{ padding: '12px 16px', textAlign: 'center', fontSize: '14px', color: '#64748b' }}>
                                  {nutIndex === 0 && <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{ingredient.medium || 0} gm</div>}
                                  {calculateNutrition(ingredient, 'medium')[nutIndex]} gm
                                </td>
                                <td style={{ padding: '12px 16px', textAlign: 'center', fontSize: '14px', color: '#64748b' }}>
                                  {nutIndex === 0 && <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{ingredient.large || 0} gm</div>}
                                  {calculateNutrition(ingredient, 'large')[nutIndex]} gm
                                </td>
                                {nutIndex === 0 && (
                                  <td rowSpan={ingredient.nutrition.length} style={{ padding: '16px', textAlign: 'center', fontWeight: '700', color: '#059669', background: '#f0fdf4', fontSize: '15px', borderLeft: '1px solid #dcfce7' }}>
                                    {calculateTotalNutritionForSize(currentSizeFilter, nut.nutrition_name)} gm
                                  </td>
                                )}
                              </tr>
                            ));
                          })}
                          
                          {/* Addons Formatting */}
                          {Object.entries(addonIngredients).map(([addonName, ingredients]) =>
                            ingredients.map((ingredient, ingIndex) => {
                              if (!ingredient.nutrition || ingredient.nutrition.length === 0) {
                                return (
                                  <tr key={`addon-${addonName}-${ingIndex}-no-nut`} style={{ borderBottom: '2px solid #f1f5f9' }}>
                                    <td style={{ padding: '16px', borderRight: '1px solid #f1f5f9', verticalAlign: 'top' }}>
                                      <div style={{ fontWeight: '700', color: '#8b5cf6', fontSize: '15px' }}>{ingredient.name}</div>
                                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '6px' }}>
                                        <span style={{ fontSize: '11px', color: '#6d28d9', background: '#ede9fe', padding: '3px 8px', borderRadius: '12px' }}>{addonName}</span>
                                        <span style={{ fontSize: '11px', color: '#64748b', background: '#f8fafc', padding: '3px 8px', borderRadius: '12px' }}>Base: {ingredient.weight || 0} gm</span>
                                      </div>
                                    </td>
                                    <td style={{ padding: '16px', color: '#94a3b8' }}>-</td>
                                    <td style={{ padding: '16px', textAlign: 'center' }}><div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px' }}>{ingredient.small || 0} gm</div><span style={{ color: '#cbd5e1' }}>-</span></td>
                                    <td style={{ padding: '16px', textAlign: 'center' }}><div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px' }}>{ingredient.medium || 0} gm</div><span style={{ color: '#cbd5e1' }}>-</span></td>
                                    <td style={{ padding: '16px', textAlign: 'center' }}><div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px' }}>{ingredient.large || 0} gm</div><span style={{ color: '#cbd5e1' }}>-</span></td>
                                    <td style={{ padding: '16px', textAlign: 'center', background: '#f8fafc', color: '#94a3b8' }}>-</td>
                                  </tr>
                                );
                              }
                              return ingredient.nutrition.map((nut, nutIndex) => (
                                <tr key={`addon-${addonName}-${ingIndex}-${nutIndex}`} style={{ borderBottom: nutIndex === ingredient.nutrition.length - 1 ? '2px solid #e2e8f0' : '1px solid #f1f5f9' }}>
                                  {nutIndex === 0 && (
                                    <td rowSpan={ingredient.nutrition.length} style={{ padding: '16px', borderRight: '1px solid #f1f5f9', verticalAlign: 'top', background: '#faf5ff' }}>
                                      <div style={{ fontWeight: '700', color: '#8b5cf6', fontSize: '15px' }}>{ingredient.name}</div>
                                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '6px' }}>
                                        <span style={{ fontSize: '11px', color: '#6d28d9', background: '#ede9fe', padding: '3px 8px', borderRadius: '12px' }}>{addonName}</span>
                                        <span style={{ fontSize: '11px', color: '#64748b', background: '#f1f5f9', padding: '3px 8px', borderRadius: '12px' }}>Base: {ingredient.weight || 0} gm</span>
                                      </div>
                                    </td>
                                  )}
                                  <td style={{ padding: '12px 16px', fontSize: '14px', fontWeight: '600', color: '#475569' }}>{nut.nutrition_name}</td>
                                  <td style={{ padding: '12px 16px', textAlign: 'center', fontSize: '14px', color: '#64748b' }}>
                                    {nutIndex === 0 && <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{ingredient.small || 0} gm</div>}
                                    {calculateNutrition(ingredient, 'small', true, addonName)[nutIndex]} gm
                                  </td>
                                  <td style={{ padding: '12px 16px', textAlign: 'center', fontSize: '14px', color: '#64748b' }}>
                                    {nutIndex === 0 && <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{ingredient.medium || 0} gm</div>}
                                    {calculateNutrition(ingredient, 'medium', true, addonName)[nutIndex]} gm
                                  </td>
                                  <td style={{ padding: '12px 16px', textAlign: 'center', fontSize: '14px', color: '#64748b' }}>
                                    {nutIndex === 0 && <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{ingredient.large || 0} gm</div>}
                                    {calculateNutrition(ingredient, 'large', true, addonName)[nutIndex]} gm
                                  </td>
                                  {nutIndex === 0 && (
                                    <td rowSpan={ingredient.nutrition.length} style={{ padding: '16px', textAlign: 'center', fontWeight: '700', color: '#059669', background: '#f0fdf4', fontSize: '15px', borderLeft: '1px solid #dcfce7' }}>
                                      {calculateTotalNutritionForSize(currentSizeFilter, nut.nutrition_name)} gm
                                    </td>
                                  )}
                                </tr>
                              ));
                            })
                          )}
                          
                          {/* Combos Formatting */}
                          {Object.entries(comboIngredients).map(([comboName, ingredients]) =>
                            ingredients.map((ingredient, ingIndex) => {
                              if (!ingredient.nutrition || ingredient.nutrition.length === 0) {
                                return (
                                  <tr key={`combo-${comboName}-${ingIndex}-no-nut`} style={{ borderBottom: '2px solid #f1f5f9' }}>
                                    <td style={{ padding: '16px', borderRight: '1px solid #f1f5f9', verticalAlign: 'top' }}>
                                      <div style={{ fontWeight: '700', color: '#f59e0b', fontSize: '15px' }}>{ingredient.name}</div>
                                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '6px' }}>
                                        <span style={{ fontSize: '11px', color: '#b45309', background: '#fef3c7', padding: '3px 8px', borderRadius: '12px' }}>{comboName}</span>
                                        <span style={{ fontSize: '11px', color: '#64748b', background: '#f8fafc', padding: '3px 8px', borderRadius: '12px' }}>Base: {ingredient.weight || 0} gm</span>
                                      </div>
                                    </td>
                                    <td style={{ padding: '16px', color: '#94a3b8' }}>-</td>
                                    <td style={{ padding: '16px', textAlign: 'center' }}><div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px' }}>{ingredient.small || 0} gm</div><span style={{ color: '#cbd5e1' }}>-</span></td>
                                    <td style={{ padding: '16px', textAlign: 'center' }}><div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px' }}>{ingredient.medium || 0} gm</div><span style={{ color: '#cbd5e1' }}>-</span></td>
                                    <td style={{ padding: '16px', textAlign: 'center' }}><div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px' }}>{ingredient.large || 0} gm</div><span style={{ color: '#cbd5e1' }}>-</span></td>
                                    <td style={{ padding: '16px', textAlign: 'center', background: '#f8fafc', color: '#94a3b8' }}>-</td>
                                  </tr>
                                );
                              }
                              return ingredient.nutrition.map((nut, nutIndex) => (
                                <tr key={`combo-${comboName}-${ingIndex}-${nutIndex}`} style={{ borderBottom: nutIndex === ingredient.nutrition.length - 1 ? '2px solid #e2e8f0' : '1px solid #f1f5f9' }}>
                                  {nutIndex === 0 && (
                                    <td rowSpan={ingredient.nutrition.length} style={{ padding: '16px', borderRight: '1px solid #f1f5f9', verticalAlign: 'top', background: '#fffbeb' }}>
                                      <div style={{ fontWeight: '700', color: '#f59e0b', fontSize: '15px' }}>{ingredient.name}</div>
                                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '6px' }}>
                                        <span style={{ fontSize: '11px', color: '#b45309', background: '#fef3c7', padding: '3px 8px', borderRadius: '12px' }}>{comboName}</span>
                                        <span style={{ fontSize: '11px', color: '#64748b', background: '#f1f5f9', padding: '3px 8px', borderRadius: '12px' }}>Base: {ingredient.weight || 0} gm</span>
                                      </div>
                                    </td>
                                  )}
                                  <td style={{ padding: '12px 16px', fontSize: '14px', fontWeight: '600', color: '#475569' }}>{nut.nutrition_name}</td>
                                  <td style={{ padding: '12px 16px', textAlign: 'center', fontSize: '14px', color: '#64748b' }}>
                                    {nutIndex === 0 && <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{ingredient.small || 0} gm</div>}
                                    {calculateNutrition(ingredient, 'small', false, null, true, comboName)[nutIndex]} gm
                                  </td>
                                  <td style={{ padding: '12px 16px', textAlign: 'center', fontSize: '14px', color: '#64748b' }}>
                                    {nutIndex === 0 && <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{ingredient.medium || 0} gm</div>}
                                    {calculateNutrition(ingredient, 'medium', false, null, true, comboName)[nutIndex]} gm
                                  </td>
                                  <td style={{ padding: '12px 16px', textAlign: 'center', fontSize: '14px', color: '#64748b' }}>
                                    {nutIndex === 0 && <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{ingredient.large || 0} gm</div>}
                                    {calculateNutrition(ingredient, 'large', false, null, true, comboName)[nutIndex]} gm
                                  </td>
                                  {nutIndex === 0 && (
                                    <td rowSpan={ingredient.nutrition.length} style={{ padding: '16px', textAlign: 'center', fontWeight: '700', color: '#059669', background: '#f0fdf4', fontSize: '15px', borderLeft: '1px solid #dcfce7' }}>
                                      {calculateTotalNutritionForSize(currentSizeFilter, nut.nutrition_name)} gm
                                    </td>
                                  )}
                                </tr>
                              ));
                            })
                          )}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {(() => {
          if (showAddonVariantPopup || showAddonCustomVariantPopup || showAddonToppingsPopup) {
            const activeAddonName = showAddonVariantPopup || showAddonCustomVariantPopup || showAddonToppingsPopup;
            const addon = fetchedItem.addons?.find(a => a.name1 === activeAddonName);
            if (!addon) return null;
            return (
              <>
                                    {showAddonVariantPopup === addon.name1 && (
                                      <div className="modal-overlay" style={{ zIndex: 1200 }} onClick={e => { e.stopPropagation(); setShowAddonVariantPopup(null); }}>
                                        <div className="modal-content p-4 shadow rounded" style={{ maxWidth: "340px", width: "95%", textAlign: "center", background: "#fff", border: "none", maxHeight: "85vh", overflowY: "auto" }} onClick={e => e.stopPropagation()}>
                                          <h5 className="mb-4 text-dark" style={{ fontWeight: 400, fontSize: "18px" }}>Select Predefined Variants <br/><small className="text-muted fs-6">({addon.name1})</small></h5>
                                          {addon.size?.enabled && (
                                            <div className="mb-3">
                                              <div className="mb-2" style={{ fontSize: "14px", color: "#333" }}>Size</div>
                                              <div className="d-flex flex-column gap-2">
                                                {[{key:"S", label:"Small", price: addon.size.small_price}, {key:"M", label:"Medium", price: addon.size.medium_price}, {key:"L", label:"Large", price: addon.size.large_price}].map(opt => (
                                                  <div key={opt.key} className="p-2 rounded border"
                                                    style={{ cursor: "pointer", minWidth: "70px", transition: "all 0.2s", borderColor: selectedAddonVariants[addon.name1]?.size === opt.key ? "#28a745" : "#ced4da", backgroundColor: selectedAddonVariants[addon.name1]?.size === opt.key ? "#e8f5e9" : "#fff", borderWidth: selectedAddonVariants[addon.name1]?.size === opt.key ? "2px" : "1px" }}
                                                    onClick={e => { e.stopPropagation(); setSelectedAddonVariants(prev => ({ ...prev, [addon.name1]: { ...prev[addon.name1], size: opt.key } })); }}>
                                                    <div style={{ fontWeight: "bold", fontSize: "14px", color: "#212529" }}>{opt.key} ({currencySymbol}{opt.price || 0})</div>
                                                  </div>
                                                ))}
                                              </div>
                                            </div>
                                          )}
                                          {addon.cold?.enabled && (
                                            <div className="mb-3">
                                              <div className="mb-2" style={{ fontSize: "14px", color: "#333" }}>Ice Preference</div>
                                              <div className="d-flex flex-column gap-2">
                                                {[{key:"without_ice", label:"Without Ice", price:0}, {key:"with_ice", label:"With Ice", price: addon.cold.ice_price}].map(opt => (
                                                  <div key={opt.key} className="p-2 rounded border"
                                                    style={{ cursor: "pointer", minWidth: "100px", transition: "all 0.2s", borderColor: selectedAddonVariants[addon.name1]?.cold === opt.key ? "#28a745" : "#ced4da", backgroundColor: selectedAddonVariants[addon.name1]?.cold === opt.key ? "#e8f5e9" : "#fff", borderWidth: selectedAddonVariants[addon.name1]?.cold === opt.key ? "2px" : "1px" }}
                                                    onClick={e => { e.stopPropagation(); setSelectedAddonVariants(prev => ({ ...prev, [addon.name1]: { ...prev[addon.name1], cold: opt.key } })); }}>
                                                    <div style={{ fontWeight: "bold", fontSize: "14px", color: "#212529" }}>{opt.label}</div>
                                                  </div>
                                                ))}
                                              </div>
                                            </div>
                                          )}
                                          {addon.spicy?.enabled && (
                                            <div className="mb-3">
                                              <div className="mb-2" style={{ fontSize: "14px", color: "#333" }}>Spicy Preference</div>
                                              <div className="d-flex flex-column gap-2">
                                                {[{key:false, label:"Non-Spicy", price: addon.spicy.non_spicy_price}, {key:true, label:"Spicy", price: addon.spicy.spicy_price || 30}].map(opt => (
                                                  <div key={String(opt.key)} className="p-2 rounded border"
                                                    style={{ cursor: "pointer", minWidth: "100px", transition: "all 0.2s", borderColor: selectedAddonVariants[addon.name1]?.spicy === opt.key ? "#28a745" : "#ced4da", backgroundColor: selectedAddonVariants[addon.name1]?.spicy === opt.key ? "#e8f5e9" : "#fff", borderWidth: selectedAddonVariants[addon.name1]?.spicy === opt.key ? "2px" : "1px" }}
                                                    onClick={e => { e.stopPropagation(); setSelectedAddonVariants(prev => ({ ...prev, [addon.name1]: { ...prev[addon.name1], spicy: opt.key } })); }}>
                                                    <div style={{ fontWeight: "bold", fontSize: "14px", color: "#212529" }}>{opt.label} ({currencySymbol}{opt.price || 0})</div>
                                                  </div>
                                                ))}
                                              </div>
                                            </div>
                                          )}
                                          {addon.sugar?.enabled && (
                                            <div className="mb-3">
                                              <div className="mb-2" style={{ fontSize: "14px", color: "#333" }}>Sugar Level</div>
                                              <div className="d-flex flex-column gap-2">
                                                {[{key:"less", label:"Less"}, {key:"medium", label:"Medium"}, {key:"extra", label:"Extra"}].map(opt => (
                                                  <div key={opt.key} className="p-2 rounded border"
                                                    style={{ cursor: "pointer", minWidth: "70px", transition: "all 0.2s", borderColor: selectedAddonVariants[addon.name1]?.sugar === opt.key ? "#28a745" : "#ced4da", backgroundColor: selectedAddonVariants[addon.name1]?.sugar === opt.key ? "#e8f5e9" : "#fff", borderWidth: selectedAddonVariants[addon.name1]?.sugar === opt.key ? "2px" : "1px" }}
                                                    onClick={e => { e.stopPropagation(); setSelectedAddonVariants(prev => ({ ...prev, [addon.name1]: { ...prev[addon.name1], sugar: opt.key } })); }}>
                                                    <div style={{ fontWeight: "bold", fontSize: "14px", color: "#212529" }}>{opt.label}</div>
                                                  </div>
                                                ))}
                                              </div>
                                            </div>
                                          )}
                                          <button className="btn w-100 rounded mt-2 text-white" style={{ backgroundColor: "#6c757d", fontWeight: "500" }} onClick={e => { e.stopPropagation(); setShowAddonVariantPopup(null); }}>
                                            Close
                                          </button>
                                        </div>
                                      </div>
                                    )}
                                    {showAddonCustomVariantPopup === addon.name1 && addon.custom_variants?.some(v => v.enabled) && (
                                      <div className="modal-overlay" style={{ zIndex: 1200 }} onClick={e => { e.stopPropagation(); setShowAddonCustomVariantPopup(null); }}>
                                        <div className="modal-content p-4 shadow rounded" style={{ maxWidth: "340px", width: "95%", textAlign: "center", background: "#fff", border: "none", maxHeight: "85vh", overflowY: "auto" }} onClick={e => e.stopPropagation()}>
                                          <h5 className="mb-4 text-dark" style={{ fontWeight: 400, fontSize: "18px" }}>Select Custom Variants <br/><small className="text-muted fs-6">({addon.name1})</small></h5>
                                          <div className="mb-3">
                                            {addon.custom_variants.map(variant => variant.enabled && (
                                              <div key={variant._id} className="mb-3">
                                                <div className="mb-2" style={{ fontSize: "14px", color: "#333" }}>{variant.heading}</div>
                                                <div className="d-flex flex-column gap-2">
                                                  {variant.subheadings.map(sub => (
                                                    <div key={sub.name} className="p-2 rounded border"
                                                      style={{ cursor: "pointer", minWidth: "120px", transition: "all 0.2s", borderColor: selectedAddonCustomVariants[addon.name1]?.[sub.name] ? "#28a745" : "#ced4da", backgroundColor: selectedAddonCustomVariants[addon.name1]?.[sub.name] ? "#e8f5e9" : "#fff", borderWidth: selectedAddonCustomVariants[addon.name1]?.[sub.name] ? "2px" : "1px" }}
                                                      onClick={e => { e.stopPropagation(); setSelectedAddonCustomVariants(prev => ({ ...prev, [addon.name1]: { ...(prev[addon.name1] || {}), [sub.name]: !prev[addon.name1]?.[sub.name] } })); }}>
                                                      <div style={{ fontWeight: "bold", fontSize: "14px", color: "#212529" }}>{sub.name} (+{currencySymbol}{sub.price || 0})</div>
                                                    </div>
                                                  ))}
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                          <button className="btn w-100 rounded mt-2 text-white" style={{ backgroundColor: "#6c757d", fontWeight: "500" }} onClick={e => { e.stopPropagation(); setShowAddonCustomVariantPopup(null); }}>
                                            Close
                                          </button>
                                        </div>
                                      </div>
                                    )}
                                    {showAddonToppingsPopup === addon.name1 && addon.toppings?.length > 0 && (
                                      <div className="modal-overlay" style={{ zIndex: 1200 }} onClick={e => { e.stopPropagation(); setShowAddonToppingsPopup(null); }}>
                                        <div className="modal-content p-4 shadow rounded" style={{ maxWidth: "340px", width: "95%", textAlign: "center", background: "#fff", border: "none", maxHeight: "85vh", overflowY: "auto" }} onClick={e => e.stopPropagation()}>
                                          <h5 className="mb-4 text-dark" style={{ fontWeight: 400, fontSize: "18px" }}>Select Toppings <br/><small className="text-muted fs-6">({addon.name1})</small></h5>
                                          <div className="mb-3">
                                            <div className="mb-2" style={{ fontSize: "14px", color: "#333" }}>Toppings</div>
                                            <div className="d-flex flex-column gap-2">
                                              {addon.toppings.map(topping => (
                                                <div key={topping._id || topping.topping_name} className="p-2 rounded border"
                                                  style={{ cursor: "pointer", minWidth: "120px", transition: "all 0.2s", borderColor: selectedAddonToppings[addon.name1]?.[(topping._id || topping.topping_name)] ? "#28a745" : "#ced4da", backgroundColor: selectedAddonToppings[addon.name1]?.[(topping._id || topping.topping_name)] ? "#e8f5e9" : "#fff", borderWidth: selectedAddonToppings[addon.name1]?.[(topping._id || topping.topping_name)] ? "2px" : "1px" }}
                                                  onClick={e => { e.stopPropagation(); setSelectedAddonToppings(prev => ({ ...prev, [addon.name1]: { ...(prev[addon.name1] || {}), [(topping._id || topping.topping_name)]: !prev[addon.name1]?.[(topping._id || topping.topping_name)] } })); }}>
                                                  <div style={{ fontWeight: "bold", fontSize: "14px", color: "#212529" }}>{topping.topping_name} (+{currencySymbol}{topping.price || 0})</div>
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                          <button className="btn w-100 rounded mt-2 text-white" style={{ backgroundColor: "#6c757d", fontWeight: "500" }} onClick={e => { e.stopPropagation(); setShowAddonToppingsPopup(null); }}>
                                            Close
                                          </button>
                                        </div>
                                      </div>
                                    )}
              </>
            );
          }
          return null;
        })()}
        {(() => {
          if (showComboVariantPopup || showComboCustomVariantPopup || showComboToppingsPopup) {
            const activeComboName = showComboVariantPopup || showComboCustomVariantPopup || showComboToppingsPopup;
            const combo = fetchedItem.combos?.find(c => c.name1 === activeComboName);
            if (!combo) return null;
            return (
              <>
                                    {showComboVariantPopup === combo.name1 && (
                                      <div className="modal-overlay" style={{ zIndex: 1200 }} onClick={e => { e.stopPropagation(); setShowComboVariantPopup(null); }}>
                                        <div className="modal-content p-4 shadow rounded" style={{ maxWidth: "340px", width: "95%", textAlign: "center", background: "#fff", border: "none", maxHeight: "85vh", overflowY: "auto" }} onClick={e => e.stopPropagation()}>
                                          <h5 className="mb-4 text-dark" style={{ fontWeight: 400, fontSize: "18px" }}>Select Predefined Variants <br/><small className="text-muted fs-6">({combo.name1})</small></h5>
                                          {combo.size?.enabled && (
                                            <div className="mb-3">
                                              <div className="mb-2" style={{ fontSize: "14px", color: "#333" }}>Size</div>
                                              <div className="d-flex flex-column gap-2">
                                                {[{key:"S", label:"Small", price: combo.size.small_price}, {key:"M", label:"Medium", price: combo.size.medium_price}, {key:"L", label:"Large", price: combo.size.large_price}].map(opt => (
                                                  <div key={opt.key} className="p-2 rounded border"
                                                    style={{ cursor: "pointer", minWidth: "70px", transition: "all 0.2s", borderColor: selectedComboVariants[combo.name1]?.size === opt.key ? "#28a745" : "#ced4da", backgroundColor: selectedComboVariants[combo.name1]?.size === opt.key ? "#e8f5e9" : "#fff", borderWidth: selectedComboVariants[combo.name1]?.size === opt.key ? "2px" : "1px" }}
                                                    onClick={e => { e.stopPropagation(); setSelectedComboVariants(prev => ({ ...prev, [combo.name1]: { ...prev[combo.name1], size: opt.key } })); }}>
                                                    <div style={{ fontWeight: "bold", fontSize: "14px", color: "#212529" }}>{opt.key} ({currencySymbol}{opt.price || 0})</div>
                                                  </div>
                                                ))}
                                              </div>
                                            </div>
                                          )}
                                          {combo.cold?.enabled && (
                                            <div className="mb-3">
                                              <div className="mb-2" style={{ fontSize: "14px", color: "#333" }}>Ice Preference</div>
                                              <div className="d-flex flex-column gap-2">
                                                {[{key:"without_ice", label:"Without Ice", price:0}, {key:"with_ice", label:"With Ice", price: combo.cold.ice_price}].map(opt => (
                                                  <div key={opt.key} className="p-2 rounded border"
                                                    style={{ cursor: "pointer", minWidth: "100px", transition: "all 0.2s", borderColor: selectedComboVariants[combo.name1]?.cold === opt.key ? "#28a745" : "#ced4da", backgroundColor: selectedComboVariants[combo.name1]?.cold === opt.key ? "#e8f5e9" : "#fff", borderWidth: selectedComboVariants[combo.name1]?.cold === opt.key ? "2px" : "1px" }}
                                                    onClick={e => { e.stopPropagation(); setSelectedComboVariants(prev => ({ ...prev, [combo.name1]: { ...prev[combo.name1], cold: opt.key } })); }}>
                                                    <div style={{ fontWeight: "bold", fontSize: "14px", color: "#212529" }}>{opt.label}</div>
                                                  </div>
                                                ))}
                                              </div>
                                            </div>
                                          )}
                                          {combo.spicy?.enabled && (
                                            <div className="mb-3">
                                              <div className="mb-2" style={{ fontSize: "14px", color: "#333" }}>Spicy Preference</div>
                                              <div className="d-flex flex-column gap-2">
                                                {[{key:false, label:"Non-Spicy", price: combo.spicy.non_spicy_price}, {key:true, label:"Spicy", price: combo.spicy.spicy_price || 30}].map(opt => (
                                                  <div key={String(opt.key)} className="p-2 rounded border"
                                                    style={{ cursor: "pointer", minWidth: "100px", transition: "all 0.2s", borderColor: selectedComboVariants[combo.name1]?.spicy === opt.key ? "#28a745" : "#ced4da", backgroundColor: selectedComboVariants[combo.name1]?.spicy === opt.key ? "#e8f5e9" : "#fff", borderWidth: selectedComboVariants[combo.name1]?.spicy === opt.key ? "2px" : "1px" }}
                                                    onClick={e => { e.stopPropagation(); setSelectedComboVariants(prev => ({ ...prev, [combo.name1]: { ...prev[combo.name1], spicy: opt.key } })); }}>
                                                    <div style={{ fontWeight: "bold", fontSize: "14px", color: "#212529" }}>{opt.label} ({currencySymbol}{opt.price || 0})</div>
                                                  </div>
                                                ))}
                                              </div>
                                            </div>
                                          )}
                                          {combo.sugar?.enabled && (
                                            <div className="mb-3">
                                              <div className="mb-2" style={{ fontSize: "14px", color: "#333" }}>Sugar Level</div>
                                              <div className="d-flex flex-column gap-2">
                                                {[{key:"less", label:"Less"}, {key:"medium", label:"Medium"}, {key:"extra", label:"Extra"}].map(opt => (
                                                  <div key={opt.key} className="p-2 rounded border"
                                                    style={{ cursor: "pointer", minWidth: "70px", transition: "all 0.2s", borderColor: selectedComboVariants[combo.name1]?.sugar === opt.key ? "#28a745" : "#ced4da", backgroundColor: selectedComboVariants[combo.name1]?.sugar === opt.key ? "#e8f5e9" : "#fff", borderWidth: selectedComboVariants[combo.name1]?.sugar === opt.key ? "2px" : "1px" }}
                                                    onClick={e => { e.stopPropagation(); setSelectedComboVariants(prev => ({ ...prev, [combo.name1]: { ...prev[combo.name1], sugar: opt.key } })); }}>
                                                    <div style={{ fontWeight: "bold", fontSize: "14px", color: "#212529" }}>{opt.label}</div>
                                                  </div>
                                                ))}
                                              </div>
                                            </div>
                                          )}
                                          <button className="btn w-100 rounded mt-2 text-white" style={{ backgroundColor: "#6c757d", fontWeight: "500" }} onClick={e => { e.stopPropagation(); setShowComboVariantPopup(null); }}>
                                            Close
                                          </button>
                                        </div>
                                      </div>
                                    )}
                                    {showComboCustomVariantPopup === combo.name1 && combo.custom_variants?.some(v => v.enabled) && (
                                      <div className="modal-overlay" style={{ zIndex: 1200 }} onClick={e => { e.stopPropagation(); setShowComboCustomVariantPopup(null); }}>
                                        <div className="modal-content p-4 shadow rounded" style={{ maxWidth: "340px", width: "95%", textAlign: "center", background: "#fff", border: "none", maxHeight: "85vh", overflowY: "auto" }} onClick={e => e.stopPropagation()}>
                                          <h5 className="mb-4 text-dark" style={{ fontWeight: 400, fontSize: "18px" }}>Select Custom Variants <br/><small className="text-muted fs-6">({combo.name1})</small></h5>
                                          <div className="mb-3">
                                            {combo.custom_variants.map(variant => variant.enabled && (
                                              <div key={variant._id} className="mb-3">
                                                <div className="mb-2" style={{ fontSize: "14px", color: "#333" }}>{variant.heading}</div>
                                                <div className="d-flex flex-column gap-2">
                                                  {variant.subheadings.map(sub => (
                                                    <div key={sub.name} className="p-2 rounded border"
                                                      style={{ cursor: "pointer", minWidth: "120px", transition: "all 0.2s", borderColor: selectedComboCustomVariants[combo.name1]?.[sub.name] ? "#28a745" : "#ced4da", backgroundColor: selectedComboCustomVariants[combo.name1]?.[sub.name] ? "#e8f5e9" : "#fff", borderWidth: selectedComboCustomVariants[combo.name1]?.[sub.name] ? "2px" : "1px" }}
                                                      onClick={e => { e.stopPropagation(); setSelectedComboCustomVariants(prev => ({ ...prev, [combo.name1]: { ...(prev[combo.name1] || {}), [sub.name]: !prev[combo.name1]?.[sub.name] } })); }}>
                                                      <div style={{ fontWeight: "bold", fontSize: "14px", color: "#212529" }}>{sub.name} (+{currencySymbol}{sub.price || 0})</div>
                                                    </div>
                                                  ))}
                                                </div>
                                              </div>
                                            ))}
                                          </div>
                                          <button className="btn w-100 rounded mt-2 text-white" style={{ backgroundColor: "#6c757d", fontWeight: "500" }} onClick={e => { e.stopPropagation(); setShowComboCustomVariantPopup(null); }}>
                                            Close
                                          </button>
                                        </div>
                                      </div>
                                    )}
                                    {showComboToppingsPopup === combo.name1 && combo.toppings?.length > 0 && (
                                      <div className="modal-overlay" style={{ zIndex: 1200 }} onClick={e => { e.stopPropagation(); setShowComboToppingsPopup(null); }}>
                                        <div className="modal-content p-4 shadow rounded" style={{ maxWidth: "340px", width: "95%", textAlign: "center", background: "#fff", border: "none", maxHeight: "85vh", overflowY: "auto" }} onClick={e => e.stopPropagation()}>
                                          <h5 className="mb-4 text-dark" style={{ fontWeight: 400, fontSize: "18px" }}>Select Toppings <br/><small className="text-muted fs-6">({combo.name1})</small></h5>
                                          <div className="mb-3">
                                            <div className="mb-2" style={{ fontSize: "14px", color: "#333" }}>Toppings</div>
                                            <div className="d-flex flex-column gap-2">
                                              {combo.toppings.map(topping => (
                                                <div key={topping._id || topping.topping_name} className="p-2 rounded border"
                                                  style={{ cursor: "pointer", minWidth: "120px", transition: "all 0.2s", borderColor: selectedComboToppings[combo.name1]?.[(topping._id || topping.topping_name)] ? "#28a745" : "#ced4da", backgroundColor: selectedComboToppings[combo.name1]?.[(topping._id || topping.topping_name)] ? "#e8f5e9" : "#fff", borderWidth: selectedComboToppings[combo.name1]?.[(topping._id || topping.topping_name)] ? "2px" : "1px" }}
                                                  onClick={e => { e.stopPropagation(); setSelectedComboToppings(prev => ({ ...prev, [combo.name1]: { ...(prev[combo.name1] || {}), [(topping._id || topping.topping_name)]: !prev[combo.name1]?.[(topping._id || topping.topping_name)] } })); }}>
                                                  <div style={{ fontWeight: "bold", fontSize: "14px", color: "#212529" }}>{topping.topping_name} (+{currencySymbol}{topping.price || 0})</div>
                                                </div>
                                              ))}
                                            </div>
                                          </div>
                                          <button className="btn w-100 rounded mt-2 text-white" style={{ backgroundColor: "#6c757d", fontWeight: "500" }} onClick={e => { e.stopPropagation(); setShowComboToppingsPopup(null); }}>
                                            Close
                                          </button>
                                        </div>
                                      </div>
                                    )}
              </>
            );
          }
          return null;
        })()}
      </>
    </ErrorBoundary>
  );
};

FoodDetails.propTypes = {
  item: PropTypes.shape({
    _id: PropTypes.string,
    name: PropTypes.string,
    item_name: PropTypes.string,
    item_group: PropTypes.string,
    price_list_rate: PropTypes.number,
    image: PropTypes.string,
    images: PropTypes.arrayOf(PropTypes.string),
    ingredients: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
      base_price: PropTypes.number,
      nutrition: PropTypes.arrayOf(PropTypes.shape({
        nutrition_name: PropTypes.string,
        nutrition_value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      })),
    })),
    size: PropTypes.shape({
      enabled: PropTypes.bool,
      small_price: PropTypes.number,
      medium_price: PropTypes.number,
      large_price: PropTypes.number,
    }),
    cold: PropTypes.shape({
      enabled: PropTypes.bool,
      ice_price: PropTypes.number,
    }),
    spicy: PropTypes.shape({
      enabled: PropTypes.bool,
      spicy_price: PropTypes.number,
      non_spicy_price: PropTypes.number,
      spicy_image: PropTypes.string,
      non_spicy_image: PropTypes.string,
    }),
    sugar: PropTypes.shape({
      enabled: PropTypes.bool,
      level: PropTypes.string,
    }),
    variantDetails: PropTypes.shape({
      enabled: PropTypes.bool,
      subheadings: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string,
        price: PropTypes.number,
        image: PropTypes.string,
      })),
    }),
    addons: PropTypes.arrayOf(PropTypes.shape({
      name1: PropTypes.string,
      addon_price: PropTypes.number,
      addon_image: PropTypes.string,
      size: PropTypes.shape({
        enabled: PropTypes.bool,
        small_price: PropTypes.number,
        medium_price: PropTypes.number,
        large_price: PropTypes.number,
      }),
      spicy: PropTypes.shape({
        enabled: PropTypes.bool,
        spicy_price: PropTypes.number,
      }),
    })),
    combos: PropTypes.arrayOf(PropTypes.shape({
      name1: PropTypes.string,
      combo_price: PropTypes.number,
      combo_image: PropTypes.string,
      size: PropTypes.shape({
        enabled: PropTypes.bool,
        small_price: PropTypes.number,
        medium_price: PropTypes.number,
        large_price: PropTypes.number,
      }),
      spicy: PropTypes.shape({
        enabled: PropTypes.bool,
        spicy_price: PropTypes.number,
      }),
    })),
    offer_price: PropTypes.number,
    offer_start_time: PropTypes.string,
    offer_end_time: PropTypes.string,
    kitchen: PropTypes.string,
  }).isRequired,
  cartItem: PropTypes.shape({
    quantity: PropTypes.number,
    selectedSize: PropTypes.string,
    icePreference: PropTypes.string,
    isSpicy: PropTypes.bool,
    addonQuantities: PropTypes.object,
    addonVariants: PropTypes.object,
    comboQuantities: PropTypes.object,
    comboVariants: PropTypes.object,
    selectedCombos: PropTypes.array,
    selectedCustomVariants: PropTypes.object,
    // NEW: kitchenNotes
    kitchenNotes: PropTypes.object,
  }),
  onClose: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
};

export default FoodDetails;





